import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import Colors from '@/Colors';
import DatadogApi from '@/common/apis/DatadogApi';
import allPollens from '@/common/components/controls/targeting/pollen/PollenTargetingEnumerations';
import allUVs from '@/common/components/controls/targeting/uv/UVTargetingEnumerations';
import * as WeatherTargetingEnumerations from '@/common/components/controls/targeting/weather/WeatherTargetingEnumerations';
import { BiddingType } from '@/common/enums/BiddingType.enum';
import BudgetModes from '@/common/enums/budget-modes.enum';
import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import FrequencyCapUnit from '@/common/enums/FrequencyCapUnit.enum';
import PacingStrategyEnum from '@/common/enums/pacing-strategy.enum';
import PurchaseTypes from '@/common/enums/purchase-types.enum';
import RunState from '@/common/enums/run-state.enum';
import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
import {
  Campaign,
  LineItemMediaApproval,
  LineItemProperty,
  LineItemTargetingCriterion,
  Market,
  MediaOwnerApproval,
  MediaType,
  MembershipSettings,
  Network,
  Package,
  PurchaseTypeOptions,
  TargetingCriterion,
  UnitPack,
} from '@/common/models';
import { IEstimatedReachTargetParams } from '@/common/models/audience/ReachStatistics';
import CachedPacing from '@/common/models/CachedPacing';
import { CampaignType } from '@/common/models/campaign/Campaign';
import { ICPMNetAPI } from '@/common/models/cpm/CpmNetApi.model';
import CPMCalculator from '@/common/models/CPMCalculator';
import { daypartToRanges } from '@/common/models/Dayparting';
import EntityFee, { IEntityFeeAPI } from '@/common/models/EntityFee';
import Exception from '@/common/models/Exception';
import Flight from '@/common/models/flight/Flight';
import FrequencyCapUnitOptions from '@/common/models/FrequencyCapUnitOptions';
import {
  IEstimatedDeliveryStatsAPI,
  IInventoryTargetingAPI,
  ILineItemCPMTargetingAPI,
  ILineItemTargetingAPI,
} from '@/common/models/InventoryTargeting';
import { getEntityProperties } from '@/common/models/lineitem/LineItem.entityProperties';
import LineItemChannelType from '@/common/models/lineitem/LineItemChannelType.enum';
import LineItemDiscount, { ILineItemDiscountAPI } from '@/common/models/lineitem/LineItemDiscount';
import { ILineItemPropertyAPI } from '@/common/models/lineitem/LineItemProperty';
import LineItemPropertyKeys from '@/common/models/lineitem/LineItemPropertyKeys.enum';
import LineItemStatus from '@/common/models/lineitem/LineItemStatus.enum';
import { ILineItemTargetingCriterionAPI } from '@/common/models/lineitem/LineItemTargetingCriterion';
import MarginValue, { IMarginValueAPI } from '@/common/models/margins/MarginValue';
import Measurement, { ILineItemMeasurementAPI } from '@/common/models/measurements/Measurement';
import MediaOwner from '@/common/models/media-owner/MediaOwner';
import Organization from '@/common/models/memberships/Organization';
import PacingStrategyOptions, {
  IPacingStrategyOption,
  IPacingStrategyStrengthFactor,
} from '@/common/models/PacingStrategyOptions';
import IFromPackage from '@/common/models/package/IFromPackage';
import { IPackageAPI } from '@/common/models/package/Package';
import PackageType from '@/common/models/package/PackageType.enum';
import { IPackageGroupAPI } from '@/common/models/package-groups/PackageGroup';
import PostalCodesFile, { IPostalCodesFileAPI } from '@/common/models/postalCodesFiles/PostalCodesFile';
import {
  Filter,
  FilterCollection,
  FilterComparisonOperator,
  FilterGroup,
  FilterLogicalOperator,
  QueryParams,
} from '@/common/models/query-params';
import { INetworkFrequencyCapAPI, NetworkFrequencyCap } from '@/common/models/targeting-criteria/NetworkFrequencyCap';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import {
  DASH,
  FullDaypart,
  getDateFormatForLocale,
  getStore,
  toServerDate,
  toServerDateTime,
  toServerDateTimeNoTimezone,
  uniqBy,
} from '@/globals';
import DisplayAndVideoSSPs from '@/org-app/components/lineItems/form/DisplayAndVideoSSPs';
import LineItemChannelTypeOptions from '@/org-app/components/lineItems/LineItemChannelTypeOptions';
import { useMediaOwners, useNetworks, useTargetingCriteria } from '@/org-app/services/tanstack/queries';
import { peekQueryInScope, runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import orgAppStore from '@/org-app/store';
import rootStore from '@/store';
import * as modelMutationTypes from '@/store/modules/models/mutation-types';
import { getArrayDiff, getDiff } from '@/utils/Diff';
import formatNumber from '@/utils/NumberFormatter';
import { mapOrgAppState } from '@/utils/StateMapping';
import Validatable, { IEntityChange, IExtraValidation, IGetEntityChanges } from '@/validator/Validatable';
import Validator from '@/validator/Validator';
import i18n from '@/vueI18n';

export const LineItemTargetingType = {
  INVENTORY: 'Inventory',
  WEATHER: 'Weather',
  POLLEN: 'Pollen',
};

const type = 'LineItem';

const pacingStrategyOptions = new PacingStrategyOptions();

export interface ILineItemAPI {
  lineitem_id: number;
  channel_type: string;
  uuid: string;
  name: string;
  flight_from: string;
  flight_to: string;
  notes: string;
  ad_duration: number;
  min_seconds_between_plays: number;
  cpm_gross: number;
  budget_mode: string;
  budget?: number;
  advertiser_budget?: number;
  campaign_id: number;
  network_ids: number[];
  network_exclusion_ids: number[];
  location_ids: number[];
  location_exclusion_ids: number[];
  site_ids: number[];
  site_exclusion_ids: number[];
  unit_ids: number[];
  unit_exclusion_ids: number[];
  unit_tags: string[];
  unit_tag_exclusions: string[];
  unitpack_ids: number[];
  unitpack_exclusion_ids: number[];
  geofencegroup_ids: number[];
  market_exclusion_ids: number[];
  market_ids: number[];
  demographic_ids: number[];
  mediaowner_ids: number[];
  mediatype_ids: number[];
  mediatype_exclusion_ids: number[];
  creative_ids: number[];
  daypart: string;
  market_targeted_impressions?: number;
  state: string;
  created_at: string;
  created_by?: number;
  submitted_at?: string;
  approval_at?: string;
  approval_comment?: string;
  cancelled_at?: string;
  delivery_mode: string;
  creation_origin: string;
  package_id?: number;
  package_ids?: number[];
  packagegroup_id?: number;
  purchase_type: string;
  priority: number;
  companion_group_name?: string;
  companion_order?: number;
  lineitem_discounts: ILineItemDiscountAPI[];
  is_cpm_from_rate_card: boolean;
  run_state: string;
  lineitem_properties: ILineItemPropertyAPI[];
  external_id?: string;
  lineitem_targetingcriteria: ILineItemTargetingCriterionAPI[];
  exchange_fee_override_minimum?: number;
  exchange_fee_override_type?: string;
  exchange_fee_override_value?: number;
  max_bid_cpm: number;
  min_bid_cpm: number;
  mobilelineitem_ids: number[];
  measurement_requested: boolean;
  measurements: ILineItemMeasurementAPI[];
  min_concentration_factor: number;
  minimum_pacing?: number;
  delivery_curve: string;
  network_frequency_caps: INetworkFrequencyCapAPI[];
  cap?: number;
  organization_dsp_id?: number;
  infinite_budget: boolean;
  lineitem_margins: IMarginValueAPI[];
  creative_formats: string[];
  creative_format_exclusions: string[];
  inventory_targeting: IInventoryTargetingAPI;
  cadence?: number;
  cadence_smooth_catchup?: boolean;
  min_concentration_value?: number;
  flight_goal_ratio?: number;
  entity_fees: IEntityFeeAPI[];
  buy_from_deals_enabled?: boolean;
  lineitem_postalcodesfiles?: IPostalCodesFileAPI[];
  postalcodesfile_ids?: number[];
  package: IPackageAPI;
  bidding_type?: string;
  is_offline_enabled?: boolean;
  delivery_rate?: string;
  lineitem_mediaowners?: number[];
  co2eEmissionsScoreThreshold?: number;
}

interface ILineItem extends IFromPackage {
  id: number;
  channelType: LineItemChannelType;
  adDuration: number;
  campaignID: number;
  campaign: Campaign;
  uuid: string;
  endDate: DateTime;
  minSecondsBetweenPlays: number;
  creativeOrientations: CreativeOrientationType[];
  creativeOrientationExclusions: CreativeOrientationType[];
  networkIDs: number[];
  networkExclusionIDs: number[];
  languageIDs: number[];
  languageExclusionIDs: number[];
  locationIDs: number[];
  locationExclusionIDs: number[];
  siteIDs: number[];
  siteExclusionIDs: number[];
  unitIDs: number[];
  unitExclusionIDs: number[];
  unitTags: string[];
  unitTagExclusions: string[];
  unitPackExclusionIDs: number[];
  unitPackIDs: number[];
  geofenceGroupIDs: number[];
  geofenceGroupExclusionIDs: number[];
  marketIDs: number[];
  marketExclusionIDs: number[];
  demographicIDs: number[];
  mediaOwnerIDs: number[];
  mediaTypeIDs: number[];
  mediaTypeExclusionIDs: number[];
  creativeIDs: number[];
  inventoryTargetingID: number;
  creativeFormats: string[];
  creativeFormatExclusions: string[];
  iabCategories: string[];
  iabCategoryExclusions: string[];
  daypart: string;
  budgetMode: BudgetModes;
  budget?: number;
  advertiserBudget?: number;
  name: string;
  startDate: DateTime;
  notes: string;
  targetImpressions?: number;
  state: LineItemStatus;
  createdOn: DateTime;
  submittedOn?: DateTime;
  approvedOn?: DateTime;
  approvalComment?: string;
  cancelledOn?: DateTime;
  CPM: number;
  cpmNet?: ICPMNetAPI[];
  cpmGross?: number;
  cpp?: number;
  isCPMFromRateCard: boolean;
  lastStoredDiscountedCPM?: number;
  mediaOwnerApprovals: LineItemMediaApproval[];
  organization: Organization;
  organizationID: number;
  organizationDSPID?: number;
  organizationName: string;
  priority: number;
  deliveryMode: string;
  creationOrigin?: string;
  package?: Package;
  packageID?: number;
  packageIDs?: number[];
  packageInternal: Package;
  packageGroupID?: number;
  purchaseType: PurchaseTypes;
  companionGroupName?: string;
  companionOrder?: number;
  lineItemDiscounts: LineItemDiscount[];
  runState: RunState;
  lineItemTargetingCriteria: LineItemTargetingCriterion[];
  lineItemProperties: LineItemProperty[];
  externalID?: string;
  estimatedAudienceCPM?: number;
  estimatedCPM?: number;
  estimatedAverageAudienceCPM?: number;
  estimatedAverageCPM?: number;
  exchangeFeeOverrideMinimum?: number;
  exchangeFeeOverrideType?: string;
  exchangeFeeOverrideValue?: number;
  lastPlayedTime?: DateTime;
  cachedPacing?: CachedPacing;
  createdOnUtc: DateTime;
  modifiedOnUtc?: DateTime;
  maxBidCPM: number;
  minBidCPM: number;
  mobileLineItemIDs: number[];
  isMeasurementRequested: boolean;
  attachedMeasurements: Measurement[];
  minConcentrationFactor: number;
  minConcentrationValue: number;
  minPacing?: number;
  deliveryCurve: string;
  networkFrequencyCaps: NetworkFrequencyCap[];
  cadence: number;
  averageActualCadence: number;
  averageScheduledCadence: number;
  dailyGoalCap?: number;
  cadenceSmoothCatchup: boolean;
  hasInfiniteBudget: boolean;
  margins: MarginValue[];
  budgetBackup: number;
  fromLineItemID?: number;
  flightRatio?: number;
  buyFromDealsEnabled?: boolean;
  apiObject: ILineItemAPI;
  entityFees: EntityFee[];
  flights?: Flight[];
  featureKeys: string[];
  transientFeatureKeys: string[];
  lineItemPostalCodesFiles?: PostalCodesFile[];
  biddingType?: BiddingType;
  isOfflineEnabled?: boolean;
}

class LineItem extends Validatable<LineItem, number> implements IGetEntityChanges<LineItem>, IFromPackage {
  static mediaOwnersCache: MediaOwner[] = null;
  static isGettingMediaOwners = false;
  static marketsCache: Market[] = null;
  static isGettingMarkets = false;
  static networksCache: Network[] = null;
  static isGettingNetworks = false;
  static mediaTypesCache: MediaType[] = null;
  static isGettingMediaTypes = false;
  static unitPacksCache: UnitPack[] = null;
  static isGettingUnitPacks = false;
  static MaxLineItemDurationInDays = 366;
  static DurationRestrictionDate = '2019-12-20';

  type: string;
  channelType: LineItemChannelType;
  adDuration: number;
  campaignID: number;
  campaign: Campaign;
  uuid: string;
  endDate: DateTime;
  minSecondsBetweenPlays: number;
  networkIDs: number[];
  networkExclusionIDs: number[];
  locationIDs: number[];
  locationExclusionIDs: number[];
  siteIDs: number[];
  siteExclusionIDs: number[];
  unitIDs: number[];
  unitExclusionIDs: number[];
  unitTags: string[];
  unitTagExclusions: string[];
  unitPackExclusionIDs: number[];
  unitPackIDs: number[];
  geofenceGroupIDs: number[];
  geofenceGroupExclusionIDs: number[];
  marketIDs: number[];
  marketExclusionIDs: number[];
  demographicIDs: number[];
  mediaOwnerIDs: number[];
  mediaTypeIDs: number[];
  mediaTypeExclusionIDs: number[];
  creativeIDs: number[];
  inventoryTargetingID: number;
  creativeFormats: string[];
  creativeFormatExclusions: string[];
  languageIDs: number[];
  languageExclusionIDs: number[];
  creativeOrientations: CreativeOrientationType[];
  creativeOrientationExclusions: CreativeOrientationType[];
  iabCategories: string[];
  iabCategoryExclusions: string[];
  daypart: string;
  budgetMode: BudgetModes;
  budget?: number;
  advertiserBudget?: number;
  name: string;
  startDate: DateTime;
  notes: string;
  targetImpressions?: number;
  state: LineItemStatus;
  createdOn: DateTime;
  submittedOn?: DateTime;
  approvedOn?: DateTime;
  approvalComment?: string;
  cancelledOn?: DateTime;
  CPM: number;
  cpmNet: ICPMNetAPI[];
  cpp?: number;
  isCPMFromRateCard: boolean;
  lastStoredDiscountedCPM?: number;
  timestamp = DateTime.local();
  mediaOwnerApprovals: LineItemMediaApproval[];
  organization: Organization;
  organizationID: number;
  organizationDSPID?: number;
  organizationName: string;
  priority: number;
  deliveryMode: string;
  creationOrigin?: string;
  packageID?: number;
  packageIDs?: number[];
  packageInternal: Package;
  packageGroupID?: number;
  purchaseType: PurchaseTypes;
  companionGroupName?: string;
  companionOrder?: number;
  lineItemDiscounts: LineItemDiscount[];
  runState: RunState;
  lineItemTargetingCriteria: LineItemTargetingCriterion[];
  missingCreativeSizes: string[];
  lineItemProperties: LineItemProperty[];
  externalID?: string;
  estimatedAudienceCPM?: number;
  estimatedCPM?: number;
  estimatedAverageAudienceCPM?: number;
  estimatedAverageCPM?: number;
  cpmCalculator: CPMCalculator;
  exchangeFeeOverrideMinimum?: number;
  exchangeFeeOverrideType?: string;
  exchangeFeeOverrideValue?: number;
  lastPlayedTime?: DateTime;
  cachedPacing?: CachedPacing;
  createdOnUtc: DateTime;
  modifiedOnUtc?: DateTime;
  maxBidCPM: number;
  minBidCPM: number;
  mobileLineItemIDs: number[];
  isMeasurementRequested: boolean;
  attachedMeasurements: Measurement[];
  minConcentrationFactor: number;
  minConcentrationValue: number;
  minPacing?: number;
  deliveryCurve: string;
  networkFrequencyCaps: NetworkFrequencyCap[];
  cadence: number;
  averageActualCadence: number;
  averageScheduledCadence: number;
  dailyGoalCap?: number;
  cadenceSmoothCatchup: boolean;
  hasInfiniteBudget: boolean;
  margins: MarginValue[];
  apiObject: ILineItemAPI;
  budgetBackup: number;
  isInlineEdited: boolean;
  fromLineItemID?: number;
  fromCampaignID?: number;
  flightRatio?: number;
  buyFromDealsEnabled?: boolean;
  /**
   * Contains the IDs of the not yet saved PackageGroup deals.
   * These IDs are only used during the saving a of LI in order to create a PackageGroup and link it to the LI if necessary.
   */
  packageGroupDealsIDs: number[];
  cachedOriginalObject: LineItem;
  entityFees: EntityFee[];
  flights?: Flight[];
  lineItemPostalCodesFiles?: PostalCodesFile[];

  featureKeys: string[];
  transientFeatureKeys: string[];
  biddingType?: BiddingType;
  co2eEmissionsScoreThreshold?: number;
  isOfflineEnabled?: boolean;

  constructor(
    id = -1,
    campaignID = -1,
    campaign = new Campaign(),
    name = '',
    channelType: LineItemChannelType = LineItemChannelType.OOH,
    uuid = null,
    startDate = null,
    endDate = null,
    notes = null,
    adDuration = null,
    minSecondsBetweenPlays = 0,
    budgetMode = BudgetModes.BUDGET, // B = budget (money), I = impressions
    budget = 0,
    networkIDs = [],
    networkExclusionIDs = [],
    locationIDs = [],
    locationExclusionIDs = [],
    siteIDs = [],
    siteExclusionIDs = [],
    unitIDs = [],
    unitExclusionIDs = [],
    unitTagExclusions = [],
    unitTags = [],
    unitPackExclusionIDs = [],
    unitPackIDs = [],
    geofenceGroupIDs = [],
    geofenceGroupExclusionIDs = [],
    marketIDs = [],
    marketExclusionIDs = [],
    demographicIDs = [],
    mediaOwnerIDs = [],
    creativeIDs = [],
    inventoryTargetingID = -1,
    daypart = FullDaypart,
    targetImpressions = null,
    state = LineItemStatus.DRAFT,
    createdOn = null,
    submittedOn = null,
    approvedOn = null,
    approvalComment = null,
    cancelledOn = null,
    cpmGross = null,
    cpmNet = null,
    mediaOwnerApprovals = [],
    organization = null,
    organizationID = null,
    organizationName = null,
    priority = 5,
    deliveryMode = undefined,
    creationOrigin = undefined,
    packageID = null,
    pack = null,
    isCPMFromRateCard = true,
    lastStoredDiscountedCPM = null,
    purchaseType = PurchaseTypes.GUARANTEED,
    companionGroupName = null,
    companionOrder = null,
    lineItemDiscounts = [],
    organizationDSPID = null,
    runState = RunState.PLAYING,
    lineItemProperties = [],
    externalID = null,
    lineItemTargetingCriteria = [],
    exchangeFeeOverrideType = null,
    exchangeFeeOverrideValue = null,
    lastPlayedTime = null,
    cachedPacing = null,
    createdOnUtc = null,
    modifiedOnUtc = null,
    maxBidCPM = null,
    mediaTypeIDs = [],
    mediaTypeExclusionIDs = [],
    mobileLineItemIDs = [],
    isMeasurementRequested = false,
    minConcentrationFactor = 0,
    minConcentrationValue = 0,
    minPacing = null,
    deliveryCurve = 'audience',
    networkFrequencyCaps = [],
    cadence = 0,
    averageActualCadence = 0.0,
    averageScheduledCadence = 0.0,
    dailyGoalCap = undefined,
    cadenceSmoothCatchup = false,
    hasInfiniteBudget = false,
    margins = [],
    creativeFormats = [],
    creativeFormatExclusions = [],
    packageGroupID = null,
    cpp = null,
    packageIDs = null,
    languageIDs = [],
    languageExclusionIDs = [],
    creativeOrientations = [],
    creativeOrientationExclusions = [],
    iabCategories = [],
    iabCategoryExclusions = [],
    flightRatio = null,
    entityFees = null,
    buyFromDealsEnabled = false,
    flights = [],
    advertiserBudget?: number,
    attachedMeasurements = [],
    featureKeys = [],
    postalCodesFiles = [],
    deal: Package = null,
    biddingType = null,
    minBidCPM = null,
    co2eEmissionsScoreThreshold = null,
    isOfflineEnabled = false,
  ) {
    super();
    this.type = type;
    this.id = id;
    this.channelType = channelType;
    this.adDuration = adDuration;
    this.campaignID = campaignID;
    this.campaign = campaign;
    this.uuid = uuid;
    this.endDate = endDate;
    this.minSecondsBetweenPlays = minSecondsBetweenPlays;
    this.networkIDs = networkIDs;
    this.networkExclusionIDs = networkExclusionIDs;
    this.locationIDs = locationIDs;
    this.locationExclusionIDs = locationExclusionIDs;
    this.siteIDs = siteIDs;
    this.siteExclusionIDs = siteExclusionIDs;
    this.unitIDs = unitIDs;
    this.unitExclusionIDs = unitExclusionIDs;
    this.unitTags = unitTags;
    this.unitTagExclusions = unitTagExclusions;
    this.unitPackExclusionIDs = unitPackExclusionIDs;
    this.unitPackIDs = unitPackIDs;
    this.geofenceGroupIDs = geofenceGroupIDs;
    this.geofenceGroupExclusionIDs = geofenceGroupExclusionIDs;
    this.marketIDs = marketIDs;
    this.marketExclusionIDs = marketExclusionIDs;
    this.demographicIDs = demographicIDs;
    this.mediaOwnerIDs = mediaOwnerIDs;
    this.mediaTypeIDs = mediaTypeIDs;
    this.mediaTypeExclusionIDs = mediaTypeExclusionIDs;
    this.creativeIDs = creativeIDs;
    this.inventoryTargetingID = inventoryTargetingID;
    this.creativeFormats = creativeFormats;
    this.creativeFormatExclusions = creativeFormatExclusions;
    this.daypart = daypart;
    this.budgetMode = budgetMode;
    this.budget = budget;
    this.advertiserBudget = advertiserBudget;
    this.name = name;
    this.startDate = startDate;
    this.notes = notes;
    this.targetImpressions = targetImpressions;
    this.state = state;
    this.createdOn = createdOn;
    this.submittedOn = submittedOn;
    this.approvedOn = approvedOn;
    this.approvalComment = approvalComment;
    this.cancelledOn = cancelledOn;
    this.CPM = cpmGross == null ? null : cpmGross;
    this.cpmNet = cpmNet;
    this.cpp = cpp;
    this.isCPMFromRateCard = isCPMFromRateCard;
    this.lastStoredDiscountedCPM = lastStoredDiscountedCPM;
    this.timestamp = DateTime.local();
    this.mediaOwnerApprovals = mediaOwnerApprovals;
    this.organization = organization;
    this.organizationID = organizationID;
    this.organizationDSPID = organizationDSPID;
    this.organizationName = organizationName;
    this.priority = priority === null || priority === undefined ? 5 : priority;

    this.deliveryMode = deliveryMode !== undefined ? deliveryMode : 'impressions';
    this.creationOrigin = creationOrigin;
    this.packageID = packageID;
    this.packageInternal = pack;
    this.packageGroupID = packageGroupID;
    this.purchaseType = purchaseType;
    this.companionGroupName = companionGroupName;
    this.companionOrder = companionOrder;
    this.lineItemDiscounts = lineItemDiscounts;
    this.runState = runState;
    this.lineItemTargetingCriteria = lineItemTargetingCriteria;

    this.missingCreativeSizes = [];

    this.lineItemProperties = lineItemProperties;
    this.externalID = externalID;

    this.estimatedAudienceCPM = null;
    this.estimatedCPM = null;
    this.estimatedAverageAudienceCPM = null;
    this.estimatedAverageCPM = null;
    this.cpmCalculator = new CPMCalculator(this.packageInternal, this);
    this.exchangeFeeOverrideType = exchangeFeeOverrideType;
    this.exchangeFeeOverrideValue = exchangeFeeOverrideValue;
    this.lastPlayedTime = lastPlayedTime;
    this.cachedPacing = cachedPacing;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.maxBidCPM = maxBidCPM;
    this.minBidCPM = minBidCPM;
    this.mobileLineItemIDs = mobileLineItemIDs;
    this.isMeasurementRequested = isMeasurementRequested;
    this.attachedMeasurements = attachedMeasurements;
    this.minConcentrationFactor = minConcentrationFactor;
    this.minConcentrationValue = minConcentrationValue;
    this.minPacing = minPacing;
    this.deliveryCurve = deliveryCurve;
    this.networkFrequencyCaps = networkFrequencyCaps;
    this.cadence = cadence;
    this.averageActualCadence = averageActualCadence;
    this.averageScheduledCadence = averageScheduledCadence;
    this.dailyGoalCap = dailyGoalCap;
    this.cadenceSmoothCatchup = cadenceSmoothCatchup;
    this.hasInfiniteBudget = hasInfiniteBudget;
    this.margins = margins;

    this.apiObject = null;
    this.budgetBackup = 0;
    this.isInlineEdited = false;

    if (this.creationOrigin == null && LineItem.isSupplyOrganization) {
      this.creationOrigin = 'ssp';
    }

    this.fromLineItemID = null;
    this.fromCampaignID = null;
    this.packageIDs = packageIDs;

    /**
     * Contains the IDs of the not yet saved PackageGroup deals.
     * These IDs are only used during the saving a of LI in order to create a PackageGroup and link it to the LI if necessary.
     * @type {number[]}
     */
    this.packageGroupDealsIDs = null;
    this.languageIDs = languageIDs;
    this.languageExclusionIDs = languageExclusionIDs;
    this.creativeOrientations = creativeOrientations;
    this.creativeOrientationExclusions = creativeOrientationExclusions;
    this.iabCategories = iabCategories;
    this.iabCategoryExclusions = iabCategoryExclusions;

    this.flightRatio = flightRatio;
    this.flights = flights;
    this.buyFromDealsEnabled = buyFromDealsEnabled;

    this.entityFees = entityFees;
    this.featureKeys = featureKeys;
    this.transientFeatureKeys = featureKeys;
    this.lineItemPostalCodesFiles = postalCodesFiles;

    this.package = deal;
    this.biddingType = biddingType;
    this.co2eEmissionsScoreThreshold = co2eEmissionsScoreThreshold;
    this.isOfflineEnabled = isOfflineEnabled;
  }

  static initialize() {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      LineItem.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      LineItem.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      LineItem.entityPropertiesCached = null;
    });
  }

  get package(): Package {
    return this.packageInternal;
  }

  set package(x) {
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_PACKAGE}`, {
      lineItem: this,
      value: x,
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_CPM_CALCULATOR}`, {
      lineItem: this,
      value: new CPMCalculator(x, this),
    });
  }

  // Use to identify when a deal is bought from marketplace only
  get isFromPackage(): boolean {
    if (this.packageInternal?.isCuratedDeal) {
      return false;
    }
    return this.packageID != null && !(this.creationOrigin === 'auto' && this.organizationDSPID !== null);
  }

  get isFromPreferredOrNGInternalDeal(): boolean {
    // Hivestack DSP buyers
    return (
      this.isFromPackage && (this.packageInternal?.isNGInternalDeal || this.packageInternal?.isPreferredInternalDeal)
    );
  }

  get isFromPGExternalDeal(): boolean {
    return this.isFromPackage && this.packageInternal?.isPGExternalDeal;
  }

  get isBudgetModeAmount(): boolean {
    return this.budgetMode === BudgetModes.BUDGET;
  }

  get isBudgetModeImpressions(): boolean {
    return this.budgetMode === BudgetModes.IMPRESSIONS;
  }

  get isBudgetEditable(): boolean {
    return (
      (!this.isFromPackage && this.isBudgetModeAmount) ||
      (this.isFromPackage &&
        this.packageInternal !== null &&
        (this.packageInternal.budgetOverridable ||
          (!this.packageInternal.budgetOverridable &&
            this.packageInternal.budgetMin !== this.packageInternal.budgetMax)) &&
        this.isBudgetModeAmount)
    );
  }

  get isMarketTargetedImpressionsEditable(): boolean {
    return (
      (!this.isFromPackage && this.isBudgetModeImpressions) ||
      (this.isFromPackage &&
        this.packageInternal !== null &&
        (this.packageInternal.marketTargetedImpressionsOverridable ||
          (!this.packageInternal.marketTargetedImpressionsOverridable &&
            this.packageInternal.marketTargetedImpressionsMin !== this.packageInternal.marketTargetedImpressionsMax)) &&
        this.isBudgetModeImpressions)
    );
  }

  get displayName(): string {
    return `${this.id} - ${this.name}`;
  }

  get isMissingCreativeSizes(): boolean {
    return this.missingCreativeSizes.length > 0;
  }

  get endpoint(): string {
    return `/lineitems/${this.id}`;
  }

  get startDateFormatted(): string {
    return this.startDate.toLocaleString(DateTime.DATE_SHORT);
  }

  get lastStoredDiscountedCPMFormatted(): string {
    return this.cpmCalculator.lastStoredDiscountedCPMFormatted;
  }

  get lastPlayedTimeFormatted(): string {
    return this.lastPlayedTime != null ? this.lastPlayedTime.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get endDateFormatted(): string {
    return this.endDate.toLocaleString(DateTime.DATE_SHORT);
  }

  get organizationNameFormatted(): string {
    if (this.creationOrigin === 'dsp' || this.creationOrigin === 'auto') {
      return this.organizationName;
    }
    return DASH;
  }

  get hasAudienceTargeting(): boolean {
    return this.lineItemProperties.some((x) => x.key === 'beta-audience') || this.demographicIDs.length > 0;
  }

  /**
   * This is used for flighted Line Items
   * Those LI cachedPacing only contains the data for the running flight
   * The completed LI don't have any data and will show 0 as progress, even though there was some ad/impressions spent
   * This method will calculate the progress based on the (total spend/impressions) / goal
   */
  get uiProgress(): number {
    const denominator = this.budgetMode === BudgetModes.BUDGET ? this.budget : this.targetImpressions;
    const budgetNumerator = LineItem.isSupplyOrganization
      ? this.cachedPacing?.totalSpendOwner
      : this.cachedPacing?.totalSpendBuyer;
    const numerator = this.budgetMode === BudgetModes.BUDGET ? budgetNumerator : this.cachedPacing?.totalImpressions;
    return denominator === 0 ? 0 : numerator / denominator;
  }

  get pacing(): number {
    if (this.cachedPacing != null) {
      if (this.cachedPacing.totalImpressions === 0) {
        return null;
      }
      return this.cachedPacing.pacing;
    }

    return null;
  }

  get pacingFormatted(): string {
    if (this.pacing === null || this.pacing === 0) {
      return DASH;
    }

    return formatNumber(this.pacing, '0.00%');
  }

  get costPerPlayFormatted(): string {
    if (this.cpp == null || Number.isNaN(this.cpp)) {
      return DASH;
    }

    return formatNumber(this.cpp, '$0.00');
  }

  get cadenceFormatted(): string {
    return this.hasCadencePurchaseType ? `${this.cadence} ${i18n.global.t('names.playPerHour', { n: 2 })}` : DASH;
  }

  get actualCadenceFormatted(): string {
    return this.hasCadencePurchaseType && this.averageActualCadence >= 0
      ? `${Math.trunc(this.averageActualCadence)} ${i18n.global.t('names.playPerHour', { n: 2 })}`
      : DASH;
  }

  get scheduledCadenceFormatted(): string {
    return this.hasCadencePurchaseType && this.averageScheduledCadence >= 0
      ? `${Math.trunc(this.averageScheduledCadence)} ${i18n.global.t('names.playPerHour', { n: 2 })}`
      : DASH;
  }

  get progressFormatted(): string {
    let progress = 0;

    if (this.isUsingFlights && this.status === 'completed') {
      progress = this.uiProgress;
    } else if (this.cachedPacing != null) {
      progress = this.cachedPacing.progress;
    }

    if (
      progress !== 0 ||
      (progress === 0 && this.state !== 'C' && this.state !== 'D' && this.state !== 'R' && this.state !== 'P')
    ) {
      return formatNumber(progress, '0.00%');
    }
    return DASH;
  }

  get pacingVsEven(): number {
    if (this.isUsingFlights && this.status === 'completed') {
      return this.uiProgress;
    }

    if (this.cachedPacing != null && this.durationInHours !== 0) {
      if (this.isBudgetModeAmount) {
        if (this.budget !== 0) {
          return (
            this.cachedPacing.totalSpendBuyer /
            ((this.budget / this.durationInHours) * Math.floor(this.ellapsedInHours))
          );
        }
      } else if (this.isBudgetModeImpressions) {
        if (this.targetImpressions !== 0) {
          return (
            this.cachedPacing.totalImpressions /
            ((this.targetImpressions / this.durationInHours) * Math.floor(this.ellapsedInHours))
          );
        }
      }
    }
    return null;
  }

  get pacingVsEvenFormatted(): string {
    if (this.pacingVsEven == null || Number.isNaN(this.pacingVsEven)) {
      return DASH;
    }
    return formatNumber(this.pacingVsEven, '0.00%');
  }

  get dealIDFormatted(): string {
    if (this.curatedDealIDProperty !== undefined) {
      return this.curatedDealIDProperty.value;
    }
    if (this.dealIDProperty !== undefined) {
      return this.dealIDProperty.value;
    }
    if (this.dealIDsProperty !== undefined && this.dealIDsProperty.value != null) {
      return this.dealIDsProperty.value.split(',').join(', ');
    }
    if (this.dealIDsBySspProperty?.value) {
      const dealIDsBySspProperty = JSON.parse(this.dealIDsBySspProperty.value);
      const deals = [];

      Object.keys(dealIDsBySspProperty).forEach((id) => {
        const ssp = DisplayAndVideoSSPs.getAvailableSSPs().find((x) => x.id === parseInt(id, 10));
        deals.push(`${ssp?.name}: ${dealIDsBySspProperty[id].join(', ')}`);
      });

      return deals.join(', ');
    }
    return this.packageInternal === null || this.packageInternal === undefined
      ? DASH
      : this.packageInternal.dealIDFormatted;
  }

  get status(): string {
    let status = null;
    switch (this.state) {
      case LineItemStatus.DRAFT:
        status = 'draft';
        break;
      case LineItemStatus.PENDING_APPROVAL:
        status = 'pendingApproval';
        break;
      case LineItemStatus.APPROVED:
        if (this.hasEnded) {
          status = 'completed';
        } else if (this.hasStopped) {
          status = 'paused';
        } else if (this.isUpcoming) {
          status = 'upcoming';
        } else {
          status = 'active';
        }
        break;
      case LineItemStatus.REJECTED:
        status = 'rejected';
        break;
      case LineItemStatus.CANCELLED:
        status = 'archived';
        break;
      case LineItemStatus.SUBMITTED:
        if (this.hasEnded) {
          status = 'completed';
        } else if (this.mediaOwnerApprovals.length === 0 && this.isChannelTypeDOOH) {
          status = 'pendingApproval';
        } else if (
          this.mediaOwnerApprovals.filter((x) => x.state === 'A').length > 0 ||
          this.isChannelTypeDisplayVideo
        ) {
          if (this.hasStopped) {
            status = 'paused';
          } else if (this.isUpcoming) {
            status = 'upcoming';
          } else {
            status = 'active';
          }
        } else if (this.mediaOwnerApprovals.every((x) => x.state === 'R')) {
          status = 'rejected';
        } else {
          status = 'pendingApproval';
        }
        break;
      default:
        break;
    }
    return status;
  }

  get pendingApprovalEndpoint(): string {
    return `/pendingapprovals/lineitems/${this.id}`;
  }

  get auditStatus(): string {
    let status = null;
    switch (this.state) {
      case LineItemStatus.DRAFT:
        status = 'draft';
        break;
      case LineItemStatus.PENDING_APPROVAL:
        status = 'pendingApproval';
        break;
      case LineItemStatus.APPROVED:
        if (this.hasEnded) {
          status = 'completed';
        } else if (this.hasStopped) {
          status = 'paused';
        } else if (this.isUpcoming) {
          status = 'upcoming';
        } else {
          status = 'active';
        }
        break;
      case LineItemStatus.REJECTED:
        status = 'rejected';
        break;
      case LineItemStatus.CANCELLED:
        status = 'archived';
        break;
      case LineItemStatus.SUBMITTED:
        status = 'submitted';
        break;
      default:
        break;
    }
    return status;
  }

  get statusColor(): string {
    switch (this.status) {
      case 'completed':
      case 'active':
        return Colors.success;
      case 'upcoming':
        return Colors.success;
      case 'rejected':
        return Colors.error;
      case 'pendingApproval':
      case 'paused':
        return Colors.warning;
      case 'draft':
        return '#ccc';
      default:
        return '#ccc';
    }
  }

  get statusDescription(): string {
    return capitalize(i18n.global.t(`states.${this.status}`));
  }

  get hasRejections(): boolean {
    return this.mediaOwnerApprovals.filter((x) => x.state === 'R').length > 0;
  }

  get origin(): string {
    if (this.creationOrigin === 'dsp') {
      if (this.packageID === null) {
        return capitalize(i18n.global.t('names.dsp', { n: 1 }));
      }
      return capitalize(i18n.global.t('names.deal', { n: 1 }));
    }
    if (this.creationOrigin === 'auto') {
      return capitalize(i18n.global.t('names.deal', { n: 1 }));
    }
    return capitalize(i18n.global.t('names.adServer', { n: 1 }));
  }

  get purchaseTypeName(): string {
    let purchaseTypeOptions = PurchaseTypeOptions.options();
    if (
      this.purchaseType === PurchaseTypes.CADENCE &&
      mapOrgAppState().organization != null &&
      mapOrgAppState().organization.isDemandOrganization
    ) {
      purchaseTypeOptions = PurchaseTypeOptions.options(true);
    }
    // When a purchase type is disabled from the org, it will throw an error in download/ list's view as purchase type will be missing from all options.
    const option = purchaseTypeOptions.find((x) => x.value === this.purchaseType);
    return option != null ? option.name : DASH;
  }

  get reversedPriority(): number {
    if (this.priority != null) return 10 - this.priority;
    return null;
  }

  get formattedPriority(): string {
    return this.purchaseType !== PurchaseTypes.NON_GUARANTEED && this.purchaseType !== PurchaseTypes.PREFERRED
      ? this.priority.toString()
      : DASH;
  }

  get formattedReversedPriority(): string {
    return this.purchaseType !== PurchaseTypes.NON_GUARANTEED && this.purchaseType !== PurchaseTypes.PREFERRED
      ? `${this.reversedPriority}`
      : DASH;
  }

  get companionOrderFormatted(): string {
    switch (this.companionOrder) {
      case -1:
        return capitalize(i18n.global.t('states.before'));
      case 0:
        return capitalize(i18n.global.t('names.primary', { n: 1 }));
      case 1:
        return capitalize(i18n.global.t('states.after'));
      default:
        return DASH;
    }
  }

  get totalDiscount(): number {
    return 1 - this.lineItemDiscounts.reduce((mem, x) => mem * (1 - x.value), 1);
  }

  get budgetFormatted(): string {
    return this.hasInfiniteBudget ? null : this.cpmCalculator.budgetFormatted;
  }

  get budgetFormattedShort(): string {
    return this.hasInfiniteBudget ? null : this.cpmCalculator.budgetFormattedShort;
  }

  get targetImpressionsFormatted(): string {
    return this.hasInfiniteBudget ? null : this.cpmCalculator.estimatedTargetImpressionsFormatted;
  }

  //
  // Historical Impressions / Ad spend / CPM
  //
  get totalImpressionsFormatted(): string {
    return this.cachedPacing != null ? this.cachedPacing.totalImpressionsFormatted : DASH;
  }

  get totalImpressionsFormattedShort(): string {
    return this.cachedPacing != null ? this.cachedPacing.totalImpressionsFormattedShort : DASH;
  }

  get totalSpendFormatted(): string {
    if (LineItem.isSupplyOrganization) {
      return this.totalSpendOwnerFormatted;
    }
    return this.totalSpendBuyerFormatted;
  }

  get totalSpendFormattedShort(): string {
    let totalSpend = null;
    if (LineItem.isSupplyOrganization) {
      totalSpend = this.cachedPacing != null ? this.cachedPacing.totalSpendOwner : totalSpend;
    } else {
      totalSpend = this.cachedPacing != null ? this.cachedPacing.totalSpendBuyer : totalSpend;
    }
    if (totalSpend !== DASH && totalSpend != null && totalSpend !== 0) {
      return formatNumber(totalSpend, '$0.0a');
    }
    return DASH;
  }

  get averageCPMFormatted(): string {
    if (LineItem.isSupplyOrganization) {
      return this.averageCPMOwnerFormatted;
    }
    return this.averageCPMBuyerFormatted;
  }

  get maxBidCPMFormatted(): string {
    if (!this.maxBidCPM) {
      return DASH;
    }
    return i18n.global.n(this.maxBidCPM, 'currency');
  }

  get minBidCPMFormatted(): string {
    return this.minBidCPM ? i18n.global.n(this.minBidCPM, 'currency') : DASH;
  }

  get cpmStrategy(): BiddingType {
    return !this.biddingType ? BiddingType.Floor : this.biddingType;
  }

  get cpmStrategyFormatted(): string {
    return capitalize(i18n.global.t(`biddingStrategy.${this.cpmStrategy}`));
  }

  //
  // =======================================
  //

  get isPlaying(): boolean {
    return this.runState === RunState.PLAYING;
  }

  get hasStopped(): boolean {
    return this.runState === RunState.STOPPED;
  }

  get isUpcoming(): boolean {
    return this.startDate.startOf('day') > DateTime.local().startOf('day');
  }

  get isUsingFlights(): boolean {
    return !!this.flights?.length || (typeof this.flightRatio === 'number' && this.flightRatio >= 0);
  }

  get hasStarted(): boolean {
    return DateTime.local().startOf('day') >= this.startDate?.startOf('day');
  }

  get hasEnded(): boolean {
    return DateTime.local().startOf('day') > this.endDate?.startOf('day');
  }

  get isRunning(): boolean {
    return this.isPlaying && this.hasStarted && !this.hasEnded;
  }

  get hasDeliveredImpressions(): boolean {
    return this.cachedPacing != null && this.cachedPacing.totalImpressions > 0;
  }

  get hasCadencePurchaseType(): boolean {
    return this.purchaseType === PurchaseTypes.CADENCE;
  }

  /**
   * @returns `true` if the Line item is from a `Cadence` purchase type and has a Budget Mode `Budget` and has started to deliver impressions
   * @see AD-157
   */
  get isAdSpendCadenceWithDeliveredImpressions(): boolean {
    return this.hasDeliveredImpressions && this.hasCadencePurchaseType && this.isBudgetModeAmount;
  }

  get supplySideCampaignName(): string {
    return this.campaign !== undefined &&
      this.campaign !== null &&
      this.creationOrigin !== 'dsp' &&
      this.creationOrigin !== 'auto'
      ? this.campaign.name
      : DASH;
  }

  get campaignName(): string {
    return this.campaign && this.campaign.name ? this.campaign.name : DASH;
  }

  get campaignType(): CampaignType {
    return this.campaign.uiCampaignType;
  }

  get createdByUserID() {
    return this.apiObject.created_by;
  }

  get campaignTypeFormatted(): string {
    return this.campaign.campaignTypeFormatted;
  }

  get accountName(): string {
    return this.campaign && this.campaign.accountName ? this.campaign.accountName : DASH;
  }

  get accountLogoUrl(): string {
    return this.campaign && this.campaign.accountLogoUrl ? this.campaign.accountLogoUrl : null;
  }

  get advertiserName(): string {
    return !this.isAdvertiserUnknown ? this.campaign.advertiserName : DASH;
  }

  get isAdvertiserUnknown(): boolean {
    return !(this.campaign !== undefined && this.campaign !== null && this.campaign.advertiserName !== 'UNKNOWN');
  }

  get advertiserDescription(): string {
    return !this.isAdvertiserUnknown ? this.campaign.advertiserDescription : DASH;
  }

  get advertiserDomain(): string {
    return !this.isAdvertiserUnknown ? this.campaign.advertiserDomain : DASH;
  }

  get advertiserLogo(): string {
    return this.campaign.advertiserLogo;
  }

  get packageName(): string {
    return this.packageInternal !== undefined && this.packageInternal !== null ? this.packageInternal.name : DASH;
  }

  get adDurationFormatted(): string {
    return `${formatNumber(this.adDuration, '0,0')} ${i18n.global.t('names.second', { n: 2 })}`;
  }

  get minSecondsBetweenPlaysFormatted(): string {
    return `${formatNumber(this.minSecondsBetweenPlays, '0,0')} ${i18n.global.t('names.second', { n: 2 })}`;
  }

  get displayVideoFrequencyCapFormatted(): string {
    if (!this.isChannelTypeDisplayVideo || this.minSecondsBetweenPlays == null || this.minSecondsBetweenPlays === 0)
      return DASH;

    const unitName = FrequencyCapUnitOptions.options().find(
      (x) => x.id === parseInt(`${this.frequencyCapExposurePerUnit}`, 10),
    )?.name;
    return i18n.global.t(
      this.frequencyCapExposureCount === 1
        ? 'messages.frequencyCapFrequencyPerUnitOneExposure'
        : 'messages.frequencyCapFrequencyPerUnit',
      [this.frequencyCapExposureCount, `${this.frequencyCapExposurePerUnitCount} ${unitName}`],
    );
  }

  get displayVideoFrequencyCapFormattedShort(): string {
    if (!this.isChannelTypeDisplayVideo || this.minSecondsBetweenPlays == null || this.minSecondsBetweenPlays === 0)
      return DASH;

    const unitName = FrequencyCapUnitOptions.options().find(
      (x) => x.id === parseInt(`${this.frequencyCapExposurePerUnit}`, 10),
    )?.name;
    return i18n.global.t(
      this.frequencyCapExposureCount === 1
        ? 'messages.frequencyCapFrequencyPerUnitOneExposureShort'
        : 'messages.frequencyCapFrequencyPerUnitShort',
      [this.frequencyCapExposureCount, `${this.frequencyCapExposurePerUnitCount} ${unitName}`],
    );
  }

  get frequencyCapFormatted(): string {
    return this.isChannelTypeDisplayVideo
      ? this.displayVideoFrequencyCapFormattedShort
      : this.minSecondsBetweenPlaysFormatted;
  }

  get isDeletable(): boolean {
    return (
      (this.isDraft || this.isArchived) &&
      !this.hasDeliveredImpressions &&
      (this.totalSpendBuyer === 0 || this.totalSpendOwner === 0)
    );
  }

  get isDaypartFull(): boolean {
    return this.daypart !== null && this.daypart.indexOf('0') === -1;
  }

  get isDaypartEmpty(): boolean {
    return this.daypart === null || this.daypart.indexOf('1') === -1;
  }

  get isDaypartEmptyOrFull(): boolean {
    return this.isDaypartEmpty || this.isDaypartFull;
  }

  get multiDealsParams(): IPackageGroupAPI {
    return {
      packagegroup_id: this.packageGroupDealsIDs?.length > 0 ? null : this.packageGroupID,
      package_ids: this.packageGroupDealsIDs?.length > 0 ? this.packageGroupDealsIDs : [],
    };
  }

  get reachTargetParams(): IEstimatedReachTargetParams {
    return {
      unit_ids: this.unitIDs,
      demographic_ids: this.demographicIDs,
      daypart: this.daypart,
      ad_duration: this.adDuration,
      screen_frequency_cap: this.minSecondsBetweenPlays != null ? this.minSecondsBetweenPlays : 0,
      impression_goal: Number(this.targetImpressions),
    };
  }

  get cpmTargetParams(): ILineItemCPMTargetingAPI {
    const targetParams: ILineItemCPMTargetingAPI = {
      ad_duration: this.adDuration == null || this.adDuration === 0 ? 0 : parseInt(`${this.adDuration}`, 10),
      buyer_organization_dsp_id: this.organizationDSPID,
      buyer_organization_id: this.organizationID,
      creative_format_exclusions: this.creativeFormatExclusions,
      creative_formats: this.creativeFormats,
      creative_orientation_exclusions: this.creativeOrientationExclusions,
      creative_orientations: this.creativeOrientations,
      iab_categories: this.iabCategories,
      iab_category_exclusions: this.iabCategoryExclusions,
      daypart: this.daypart,
      demographic_ids: this.demographicIDs,
      from_date: toServerDate(this.startDate),
      geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      language_exclusion_ids: this.languageExclusionIDs,
      language_ids: this.languageIDs,
      lineitem_id: this.id === null || this.id === undefined ? -1 : this.id,
      lineitem_networkfreqcaps: this.networkFrequencyCaps?.map((x) => x.toApi()) ?? [],
      location_exclusion_ids: this.locationExclusionIDs,
      location_ids: this.locationIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      market_ids: this.marketIDs,
      max_bid_cpm: this.maxBidCPM,
      min_bid_cpm: this.minBidCPM,
      measurement_requested: this.isMeasurementRequested,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      mediatype_ids: this.mediaTypeIDs,
      min_concentration_factor: this.minConcentrationFactor,
      network_exclusion_ids: this.networkExclusionIDs,
      network_ids: this.networkIDs,
      owner_ids: this.mediaOwnerIDs,
      owner_organization_ids: this.mediaOwnerIDs,
      package_exchange_id: this.packageInternal != null ? this.packageInternal.exchangeID : null,
      package_id: this.packageID,
      package_organization_id: this.packageInternal != null ? this.packageInternal.organizationID : null,
      packagegroup_id: this.packageGroupID != null ? this.packageGroupID : null,
      purchase_type: this.purchaseType,
      screen_frequency_cap: this.minSecondsBetweenPlays != null ? this.minSecondsBetweenPlays : 0,
      site_exclusion_ids: this.siteExclusionIDs,
      site_ids: this.siteIDs,
      to_date: toServerDate(this.endDate),
      unique_advertiser_id_to_check: this.campaign?.advertiser?.uniqueAdvertiser?.id || null,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_ids: this.unitIDs,
      unit_tag_exclusions: this.unitTagExclusions,
      unit_tags: this.unitTags,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      unitpack_ids: this.unitPackIDs,
      buy_from_deals_enabled: this.buyFromDealsEnabled,
      lineitem_features: this.featureKeys ?? [],
      campaign_id: this.campaignID > 0 ? this.campaignID : undefined,
      requested_for_utc: this.campaign?.createdOnUtc ?? DateTime.utc(),
    };

    if (this.entityFees != null) {
      const fees = this.entityFees.map((f) => ({
        fee_category: {
          name: f.feeCategoryName,
          fee_category_id: f.feeCategoryID,
          payer: f.feeCategoryPayer,
        },
        type: f.feeType,
        value: f.value,
      }));
      targetParams.transient_entity_fees = {
        organization_id: mapOrgAppState()?.organization?.id,
        entity_type: 'LINEITEM',
        fees,
      };
    }

    if (this.minConcentrationValue !== null) {
      targetParams.min_concentration_value = this.minConcentrationValue;
    }

    if (this.exchangeFeeOverrideType !== null) {
      targetParams.lineitem_exchange_fee_override = {
        type: this.exchangeFeeOverrideType,
        value: this.exchangeFeeOverrideValue,
      };
    }

    if (mapOrgAppState()?.organization?.isDemandOrganization) {
      targetParams.packagegroup_id = this.multiDealsParams.packagegroup_id;
      targetParams.package_ids = this.multiDealsParams.package_ids;
      if (this.lineItemTargetingCriteria != null && this.lineItemTargetingCriteria.length !== 0) {
        targetParams.targetingcriterion_ids = this.lineItemTargetingCriteria.map((t) => t.targetingCriterionID);
      }
    }
    return targetParams;
  }

  get targetParams(): ILineItemTargetingAPI {
    const obj: ILineItemTargetingAPI = {
      location_ids: this.locationIDs,
      location_exclusion_ids: this.locationExclusionIDs,
      unit_ids: this.unitIDs,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_tags: this.unitTags,
      unit_tag_exclusions: this.unitTagExclusions,
      unitpack_ids: this.unitPackIDs,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      site_ids: this.siteIDs,
      site_exclusion_ids: this.siteExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
      market_ids: this.marketIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      max_bid_cpm: this.maxBidCPM,
      min_bid_cpm: this.minBidCPM,
      network_ids: this.networkIDs,
      network_exclusion_ids: this.networkExclusionIDs,
      demographic_ids: this.demographicIDs,
      from_date: toServerDate(this.startDate),
      to_date: toServerDate(this.endDate),
      ad_duration:
        this.adDuration === null || this.adDuration === undefined || this.adDuration === 0
          ? 0
          : parseInt(`${this.adDuration}`, 10),
      daypart: this.daypart,
      screen_frequency_cap: this.minSecondsBetweenPlays != null ? this.minSecondsBetweenPlays : 0,
      owner_ids: this.mediaOwnerIDs,
      owner_organization_ids: this.mediaOwnerIDs,
      mediatype_ids: this.mediaTypeIDs,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      purchase_type: this.purchaseType,
      lineitem_networkfreqcaps: this.networkFrequencyCaps?.map((x) => x.toApi()) ?? [],
      package_id: this.packageID,
      lineitem_id: this.id,
      creative_formats: this.creativeFormats,
      creative_format_exclusions: this.creativeFormatExclusions,
      language_exclusion_ids: this.languageExclusionIDs,
      language_ids: this.languageIDs,
      creative_orientation_exclusions: this.creativeOrientationExclusions,
      creative_orientations: this.creativeOrientations,
      iab_categories: this.iabCategories,
      iab_category_exclusions: this.iabCategoryExclusions,
      unique_advertiser_id_to_check: null,
      buy_from_deals_enabled: this.buyFromDealsEnabled,
      requested_for_utc: this.campaign?.createdOnUtc ?? DateTime.utc(),
    };

    if (mapOrgAppState()?.organization?.isDemandOrganization) {
      obj.packagegroup_id = this.multiDealsParams.packagegroup_id;
      obj.package_ids = this.multiDealsParams.package_ids;
    }

    return obj;
  }

  get targetParamsEstimatedDeliveryStatistics() {
    const obj: IEstimatedDeliveryStatsAPI = {
      from_date: toServerDate(this.startDate),
      to_date: toServerDate(this.endDate),
      organization_id: this.organizationID,
      unit_targeting: {
        unique_advertiser_id_to_check: this.campaign?.advertiser?.uniqueAdvertiser?.id || null,
        creative_format_exclusions: this.creativeFormatExclusions,
        creative_formats: this.creativeFormats,
        creative_orientation_exclusions: this.creativeOrientationExclusions,
        creative_orientations: this.creativeOrientations,
        geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
        geofencegroup_ids: this.geofenceGroupIDs,
        language_exclusion_ids: this.languageExclusionIDs,
        language_ids: this.languageIDs,
        lineitem_id: this.id,
        location_exclusion_ids: this.locationExclusionIDs,
        location_ids: this.locationIDs,
        market_exclusion_ids: this.marketExclusionIDs,
        market_ids: this.marketIDs,
        mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
        mediatype_ids: this.mediaTypeIDs,
        network_exclusion_ids: this.networkExclusionIDs,
        network_ids: this.networkIDs,
        organization_id: this.organizationID,
        owner_ids: this.mediaOwnerIDs,
        package_id: this.packageID,
        purchase_type: this.purchaseType,
        site_exclusion_ids: this.siteExclusionIDs,
        site_ids: this.siteIDs,
        unit_exclusion_ids: this.unitExclusionIDs,
        unit_ids: this.unitIDs,
        unit_tag_exclusions: this.unitTagExclusions,
        unit_tags: this.unitTags,
        unitpack_exclusion_ids: this.unitPackExclusionIDs,
        unitpack_ids: this.unitPackIDs,
        buy_from_deals_enabled: this.buyFromDealsEnabled,
        iab_categories: this.iabCategories,
        iab_category_exclusions: this.iabCategoryExclusions,
        requested_for_utc: this.campaign?.createdOnUtc ?? DateTime.utc(),
      },
    };

    if (this.daypart != null) obj.daypart = this.daypart;
    if (this.demographicIDs != null) obj.demographic_ids = this.demographicIDs;
    if (this.minSecondsBetweenPlays != null) {
      obj.screen_frequency_cap = this.minSecondsBetweenPlays;
    }
    if (this.networkFrequencyCaps != null) {
      obj.network_frequency_caps = this.networkFrequencyCaps?.map((x) => x.toApi()) ?? [];
    }
    if (this.maxBidCPM != null) obj.max_bid_cpm = this.maxBidCPM;
    if (this.minBidCPM != null) obj.min_bid_cpm = this.minBidCPM;
    if (this.lineItemTargetingCriteria != null && this.lineItemTargetingCriteria.length !== 0)
      obj.targetingcriterion_ids = this.lineItemTargetingCriteria.map((t) => t.targetingCriterionID);

    if (mapOrgAppState()?.organization?.isDemandOrganization) {
      obj.unit_targeting = {
        ...obj.unit_targeting,
        ...this.multiDealsParams,
      };
    }

    return obj;
  }

  get isMobileRetargetingEnabled(): boolean {
    let enabled = false;
    this.lineItemProperties.forEach((x) => {
      if (x.key === LineItemPropertyKeys.MOBILE_RETARGETING && x.value === 'true') {
        enabled = true;
      }
    });
    return enabled;
  }

  get dealIDProperty() {
    return this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.DEAL_ID);
  }

  get curatedDealIDProperty() {
    return this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.CURATED_DEAL_ID);
  }

  get dealIDsProperty() {
    return this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.DEAL_IDS);
  }

  get dealIDSSPProperty() {
    return this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.DEAL_ID_SSP);
  }

  get dealIDsByMediaOwnerProperty() {
    return this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.DEAL_IDS_BY_MEDIAOWNER);
  }

  get dealIDsBySspProperty() {
    return this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.DEAL_IDS_BY_SSP);
  }

  get dealIDsBySsp() {
    if (this.dealIDsBySspProperty == null) {
      return DASH;
    }
    const parsedDealIDsBySspProperty: Record<string, number[]> = JSON.parse(this.dealIDsBySspProperty.value);
    return Object.entries(parsedDealIDsBySspProperty).map(
      ([id, dealIDs]) =>
        `${DisplayAndVideoSSPs.getAvailableSSPs().find((x) => `${x.id}` === id)?.name || `SSP ID ${id}`} - ${dealIDs.join(', ')}`,
    );
  }

  get isFromCuratedDeal() {
    return (
      this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.FROM_CURATED_DEAL) != null ||
      this.package?.isCuratedDeal
    );
  }

  get packageRevision() {
    return this.packageInternal != null ? this.packageInternal.revision : DASH;
  }

  get didNotPlayInTheLast24Hours() {
    if (this.hasEnded) {
      return false;
    }
    if (this.lastPlayedTime == null) {
      return false; // Unknown...
    }
    if (!this.hasDaypartEnabledInTheLast24Hours) {
      return false;
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours > 24;
  }

  get didNotPlayInTheLast12Hours() {
    if (this.hasEnded) {
      return false;
    }
    if (this.lastPlayedTime == null) {
      return false; // Unknown...
    }
    if (!this.hasDaypartEnabledInTheLast12Hours) {
      return false;
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours > 12;
  }

  get didPlayInTheLast12Hours() {
    if (this.hasEnded) {
      return false;
    }
    if (this.lastPlayedTime == null) {
      return false; // Did not start playing.
    }
    if (!this.hasDaypartEnabledInTheLast12Hours) {
      return true; // Not really playing but still should show a green thumb in the UI.
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours <= 12;
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOn() {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal() : null;
  }

  get totalSpendOwnerFormatted(): string {
    return this.cachedPacing != null ? this.cachedPacing.totalSpendOwnerFormatted : DASH;
  }

  get totalSpendBuyerFormatted(): string {
    return this.cachedPacing != null ? this.cachedPacing.totalSpendBuyerFormatted : DASH;
  }

  get averageCPMBuyerFormatted(): string {
    return this.cachedPacing != null ? this.cachedPacing.averageCPMBuyerFormatted : DASH;
  }

  get averageCPMOwnerFormatted(): string {
    return this.cachedPacing != null ? this.cachedPacing.averageCPMOwnerFormatted : DASH;
  }

  get cachedReportLastUpdatedUtc() {
    return this.cachedPacing != null ? this.cachedPacing.cachedReportLastUpdatedUtc : DASH;
  }

  get cachedReportLastUpdatedOnFormatted(): string {
    return this.cachedPacing != null ? this.cachedPacing.cachedReportLastUpdatedOnFormatted : DASH;
  }

  get totalSpendOwner() {
    return this.cachedPacing != null ? this.cachedPacing.totalSpendOwner : DASH;
  }

  get totalSpendBuyer() {
    return this.cachedPacing != null ? this.cachedPacing.totalSpendBuyer : DASH;
  }

  get averageCPMBuyer() {
    return this.cachedPacing != null ? this.cachedPacing.averageCPMBuyer : DASH;
  }

  get averageCPMOwner() {
    return this.cachedPacing != null ? this.cachedPacing.averageCPMOwner : DASH;
  }

  get hasInventoryTargeting(): boolean {
    return !(
      (this.marketIDs == null || this.marketIDs.length === 0) &&
      (this.marketExclusionIDs == null || this.marketExclusionIDs.length === 0) &&
      !this.languageIDs?.length &&
      !this.languageExclusionIDs?.length &&
      (this.locationIDs == null || this.locationIDs.length === 0) &&
      (this.locationExclusionIDs == null || this.locationExclusionIDs.length === 0) &&
      (this.mediaTypeIDs == null || this.mediaTypeIDs.length === 0) &&
      (this.mediaTypeExclusionIDs == null || this.mediaTypeExclusionIDs.length === 0) &&
      (this.networkIDs == null || this.networkIDs.length === 0) &&
      (this.networkExclusionIDs == null || this.networkExclusionIDs.length === 0) &&
      (this.unitIDs == null || this.unitIDs.length === 0) &&
      (this.unitExclusionIDs == null || this.unitExclusionIDs.length === 0) &&
      (this.siteIDs == null || this.siteIDs.length === 0) &&
      (this.siteExclusionIDs == null || this.siteExclusionIDs.length === 0) &&
      (this.geofenceGroupIDs == null || this.geofenceGroupIDs.length === 0) &&
      (this.geofenceGroupExclusionIDs == null || this.geofenceGroupExclusionIDs.length === 0) &&
      (this.unitTags == null || this.unitTags.length === 0) &&
      (this.unitPackIDs == null || this.unitPackIDs.length === 0) &&
      (this.unitPackExclusionIDs == null || this.unitPackExclusionIDs.length === 0) &&
      (this.creativeFormats == null || this.creativeFormats.length === 0) &&
      (this.creativeFormatExclusions == null || this.creativeFormatExclusions.length === 0) &&
      (this.creativeOrientations == null || this.creativeOrientations.length === 0) &&
      (this.creativeOrientationExclusions == null || this.creativeOrientationExclusions.length === 0) &&
      (this.iabCategories == null || this.iabCategories.length === 0) &&
      (this.iabCategoryExclusions == null || this.iabCategoryExclusions.length === 0)
    );
  }

  get pacingColorIndicatorClassName(): string {
    if (this.status === 'paused' || this.cachedPacing == null) {
      return 'badge-disabled';
    }

    const difference = this.minPacing - this.pacing;

    if (difference > 0.1) {
      return 'badge-danger';
    }
    if (difference > 0.01) {
      return 'badge-warning';
    }
    if (this.cachedPacing.pacing > 1.06) {
      return 'badge-success-dark';
    }

    return 'badge-success';
  }

  get pacingVsEvenColorIndicatorClassName(): string {
    if (this.status === 'paused' || this.cachedPacing == null) {
      return 'badge-disabled';
    }

    const difference = this.minPacing - this.pacingVsEven;

    if (difference > 0.1) {
      return 'badge-danger';
    }
    if (difference > 0.01) {
      return 'badge-warning';
    }
    if (this.cachedPacing.pacing > 1.06) {
      return 'badge-success-dark';
    }

    return 'badge-success';
  }

  get isDraft(): boolean {
    return this.state === LineItemStatus.DRAFT;
  }

  get isArchived(): boolean {
    return this.state === LineItemStatus.CANCELLED;
  }

  get properties() {
    return this.lineItemProperties;
  }

  get sumOfLineItemBudgets() {
    const currentLineItemBudget = (this.state !== 'C' && this.budget) || 0;
    if (this.campaign == null || this.campaign.budget == null) {
      return currentLineItemBudget;
    }
    // LI edition: Campaign budget already includes the budget saved. Thus we need to subtract saved budget to it to get a correct campaign budget
    const campaignBudget = this.budgetBackup ? this.campaign.budget - this.budgetBackup : this.campaign.budget;
    return campaignBudget + currentLineItemBudget;
  }

  get remainingBudget() {
    if (this.campaign == null || this.campaign.budgetCap == null) {
      return null;
    }
    return Math.max(0, this.campaign.budgetCap - this.sumOfLineItemBudgets);
  }

  get usedBudget() {
    if (this.campaign == null || this.campaign.budgetCap == null) {
      return null;
    }
    return this.sumOfLineItemBudgets;
  }

  get isCampaignBudgetExceeded(): boolean {
    if (this.campaign == null || this.campaign.budgetCap == null) {
      return false;
    }
    return this.campaign.budgetCap - this.sumOfLineItemBudgets < 0;
  }

  get durationInDays() {
    return this.endDate.plus({ days: 1 }).diff(this.startDate, 'days').toObject().days;
  }

  get durationInHours() {
    return this.endDate.plus({ days: 1 }).diff(this.startDate, 'hours').toObject().hours;
  }

  get ellapsedInHours() {
    if (this.hasEnded) {
      return this.durationInHours;
    }
    if (!this.hasStarted) {
      return 0;
    }
    const zone = getStore()?.state?.orgApp?.organization?.timezone;
    return DateTime.fromISO(DateTime.local().toJSDate().toISOString(), { zone })
      .diff(DateTime.fromISO(this.startDate.toFormat('yyyy-MM-dd'), { zone }), 'hours')
      .toObject().hours;
  }

  get dailyGoalCapFormatted(): string {
    if (this.dailyGoalCap == null) {
      return DASH;
    }
    return this.isBudgetModeAmount ? i18n.global.n(this.dailyGoalCap, 'currency') : i18n.global.n(this.dailyGoalCap);
  }

  get dailyGoalCapWarning() {
    let warning = null;
    if (this.dailyGoalCap != null && this.durationInDays > 0) {
      if (this.isBudgetModeAmount) {
        const min = Math.ceil(this.budget / this.durationInDays);
        if (this.dailyGoalCap < min) {
          const underspending = this.budget - this.durationInDays * this.dailyGoalCap;
          warning = `${i18n.global.t('messages.dailyGoalCapWarningForLineItems', [i18n.global.n(underspending, 'currency'), i18n.global.n(min, 'currency')])}`;
        }
      } else {
        const min = Math.ceil(this.targetImpressions / this.durationInDays);
        if (this.dailyGoalCap < min) {
          const underspending = this.targetImpressions - this.durationInDays * this.dailyGoalCap;
          warning = `${i18n.global.t('messages.dailyGoalCapWarningForLineItems', [i18n.global.n(underspending), i18n.global.n(min)])}`;
        }
      }
    }
    return warning;
  }

  get dailyBudgetGoalCap() {
    return this.dailyGoalCap != null && this.isBudgetModeAmount ? this.dailyGoalCap : null;
  }

  get dailyBudgetGoalCapFormatted(): string {
    if (this.dailyBudgetGoalCap == null) {
      return DASH;
    }
    return i18n.global.n(this.dailyBudgetGoalCap, 'currency');
  }

  get dailyImpressionsGoalCap() {
    return this.dailyGoalCap != null && this.isBudgetModeImpressions ? this.dailyGoalCap : null;
  }

  get dailyImpressionsGoalCapFormatted(): string {
    if (this.dailyImpressionsGoalCap == null) {
      return DASH;
    }
    return i18n.global.n(this.dailyImpressionsGoalCap);
  }

  get shouldGoBackToDraft() {
    return false; // HV-2838 - No changes should set the line item back to draft
    //
    // const changes = this.changesFromOriginalObject || [];
    // //
    // // HV-1358 - For storefront buyers, send back to draft as soon as something changes.
    // //
    // if (mapOrgAppState().organization.exchange.isWhiteLabeled) {
    //   return Object.entries(changes).length > 0;
    // }
    // //
    // // HV-1358 - For non-storefront buyers, only go back to draft when the following properties change:
    // // - Flight dates
    // // - Budget and/or type of budget
    // // - “Buy from deal”
    // // - Audience targeting
    // //
    // const keys = Object.keys(changes);
    // const shouldGoBackToDraft = keys.includes('flight_from')
    //   || keys.includes('flight_to')
    //   || keys.includes('budget')
    //   || keys.includes('market_targeted_impressions')
    //   || keys.includes('budget_mode')
    //   || keys.includes('demographic_ids')
    //   || (keys.includes('lineitem_properties')
    //     && changes.lineitem_properties.some((x) => x.key === 'deal-id'));
    // return shouldGoBackToDraft;
  }

  get hasDaypartEnabledInTheLast24Hours(): boolean {
    return this.hasDaypartEnabledInTheLastXHours(24);
  }

  get hasDaypartEnabledInTheLast12Hours(): boolean {
    return this.hasDaypartEnabledInTheLastXHours(12);
  }

  get isHouseAdEnabled(): boolean {
    return this.purchaseType === PurchaseTypes.HOUSE_AD;
  }

  get isTakeoverEnabled(): boolean {
    return this.purchaseType === PurchaseTypes.TAKEOVER;
  }

  get isPacingStrategyAvailable(): boolean {
    return !this.isHouseAdEnabled && !this.isTakeoverEnabled && !this.hasCadencePurchaseType && this.isChannelTypeDOOH;
  }

  get isBudgetTotalAmountAvailable(): boolean {
    return !this.isHouseAdEnabled && !(this.dealIDProperty != null && this.isBudgetModeImpressions);
  }

  get areTargetImpressionsAvailable() {
    return (
      !this.isHouseAdEnabled && !(this.dealIDProperty != null && this.isBudgetModeAmount) && this.isChannelTypeDOOH
    );
  }

  get audienceFileDescription() {
    const all = orgAppStore.modules.ReferenceData.state.demographics;
    const audienceFile = this.demographicIDs.map((id) => all.find((l) => l.id === id)).filter((x) => x != null);
    if (audienceFile.length === 0 || audienceFile.filter((x) => x === null).length > 0) {
      return DASH;
    }
    return audienceFile.map((x) => x.name).join(', ');
  }

  get pacingStrategy(): IPacingStrategyOption {
    return pacingStrategyOptions.findStrategy(`${this.deliveryCurve}`);
  }

  get pacingStrategyName(): string {
    return this.pacingStrategy?.name ?? this.deliveryCurve;
  }

  get pacingStrategyDescription(): string {
    return this.pacingStrategy?.description ?? null;
  }

  get pacingStrengthFactor(): IPacingStrategyStrengthFactor {
    return this.pacingStrategy?.strengthFactors?.find((x) => x.id === this.deliveryCurve) ?? null;
  }

  get fullPacingStrategyName(): string {
    const strengthFactorLabel = i18n.global.t('names.strengthFactor');
    const sfInitials = strengthFactorLabel
      .split(' ')
      .map((word) => capitalize(word.charAt(0)))
      .join('');

    return this.pacingStrengthFactor
      ? `${this.pacingStrategyName} (${sfInitials}${this.pacingStrengthFactor.strengthFactor})`
      : this.pacingStrategyName;
  }

  get deliveryCurveName(): string {
    return this.pacingStrategyName && this.isPacingStrategyAvailable ? this.pacingStrategyName : DASH;
  }

  get daypartRanges() {
    return daypartToRanges(this.daypart);
  }

  static get isSupplyOrganization(): boolean {
    return mapOrgAppState()?.organization?.isSupplyOrganization ?? false;
  }

  get activeNetworkFrequencyCaps() {
    const networks = peekQueryInScope(useNetworks);
    return this.networkFrequencyCaps?.filter((nfc) => networks.some((x: Network) => nfc.networkId === x.id));
  }

  get frequencyCapPerNetwork() {
    // Here we assume the query cache is already populated.
    const networks = peekQueryInScope(useNetworks);
    if (
      networks.length === 0 ||
      this.activeNetworkFrequencyCaps == null ||
      this.activeNetworkFrequencyCaps.length === 0
    ) {
      return DASH;
    }

    return this.activeNetworkFrequencyCaps
      .map((nfc) => {
        const network = networks.find((x: Network) => nfc.networkId === x.id);
        return `${network.name}: ${nfc.minSecondsBetweenPlays}s`;
      })
      .join(', ');
  }

  get isStartDateEditable(): boolean {
    const isDuplicateLineItem = this.isNew && (this.fromLineItemID != null || !this.isFromPackage);

    // Business Logic (CAM-2884)
    // Start date can be edited :
    // - when creating a duplicate LI even though the original one has delivered or has ended
    // - when no impressions have delivered (even though LI is running, etc..) (acceptance criteria of CAM-2190)
    // - when start date < end date or not published
    // - when start date from package is flexible

    if (isDuplicateLineItem) {
      return true;
    }

    if (this.hasDeliveredImpressions) {
      return false;
    }

    if (!this.hasEnded || this.isNotPublished) {
      return true;
    }

    return this.isFromPackage && !this.packageInternal.startDate.equals(this.packageInternal.startDate);
  }

  get isEndDateEditable(): boolean {
    if (this.hasDeliveredImpressions && this.isTakeoverEnabled) {
      // BRO-3109 - Cannot edit end date once delivery has started in Takeover mode
      return false;
    }

    if (this.isAdSpendCadenceWithDeliveredImpressions) {
      // AD-157 - Cannot edit end date once delivery has started in Ad spend Cadence mode
      return false;
    }

    // CAM-5635: No date restriction for LI (from a package or not)
    return true;
  }

  get isNotPublished(): boolean {
    return this.isNew || this.isDraft;
  }

  /**
   * Line Items created after `2019-12-20` should have duration restrictions
   * @see https://hivestack.atlassian.net/browse/REQ-319
   * @returns {boolean}
   */
  get hasDurationRestriction(): boolean {
    return this.createdOnUtc == null || this.createdOnUtc > DateTime.fromISO(LineItem.DurationRestrictionDate);
  }

  get originalObject(): LineItem {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = LineItem.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  get isDuplicateLineItem() {
    return this.isNew && (this.fromCampaignID != null || this.fromLineItemID != null);
  }

  get targetingCriteria(): LineItemTargetingCriterion[] {
    return this.lineItemTargetingCriteria || [];
  }

  private get stateTargetingCriteria(): TargetingCriterion[] {
    return peekQueryInScope(useTargetingCriteria);
  }

  get targetCriteriaWeather() {
    const weatherTargetingCriterion = this.stateTargetingCriteria.find((x) => x.type === TargetingCriteriaType.Weather);
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map(
              (v) =>
                v.value
                  .split(',')
                  .map((v2) => WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name) || [],
            ),
          )
          .flat()
      : null;
  }

  get targetCriteriaTemperature() {
    const weatherTargetingCriterion = this.stateTargetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Temperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaUV() {
    const weatherTargetingCriterion = this.stateTargetingCriteria.find((x) => x.type === TargetingCriteriaType.UV);
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allUVs().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationWeather() {
    const weatherTargetingCriterion = this.stateTargetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationWeather,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map(
              (v) =>
                v.value
                  .split(',')
                  .map((v2) => WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name) || [],
            ),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationTemperature() {
    const weatherTargetingCriterion = this.stateTargetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationTemperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationRain() {
    const weatherTargetingCriterion = this.stateTargetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationRain,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationSnow() {
    const weatherTargetingCriterion = this.stateTargetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationSnow,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaPollen() {
    const pollenTargetingCriterion = this.stateTargetingCriteria.find((x) => x.type === TargetingCriteriaType.Pollen);
    return pollenTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === pollenTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allPollens().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  get targetCriteriaCustomEvents() {
    const customEvents = this.stateTargetingCriteria.filter((x) => x.isUpdatePushedExternally);
    const customEventSubtypes = customEvents.flatMap((ce) => ce.subTypes);
    const customEventIDs = customEvents.map((ce) => ce.id);

    return customEvents.length
      ? this.targetingCriteria
          .filter((tc) => customEventIDs.includes(tc.targetingCriterionID))
          .map((tc) =>
            tc.values.map(
              (x) =>
                `${customEventSubtypes.find((st) => st.code === x.code).categoryName}: ${customEventSubtypes.find((st) => st.code === x.code).name}`,
            ),
          )
          .flat()
      : null;
  }

  get isBuyingCuratedDeal(): boolean {
    return this.package?.packageType === PackageType.CURATED;
  }

  get flightGoalRatioPercentage(): number {
    return this.flightRatio ? this.flightRatio / 100 : null;
  }

  get flightGoalRatioPercentageFormatted(): string {
    return `${this.flightRatio}%`;
  }

  get adDurationTypeDescription(): string {
    return capitalize(i18n.global.t(this.adDuration === 0 ? 'messages.basedOnScreen' : 'states.fixed'));
  }

  get customFields() {
    const customFields = rootStore.getters['orgApp/organizationLineItemCustomFields'];
    return this.lineItemProperties.filter((x) => customFields.some((cf) => cf.key === x.key));
  }

  get iabCategoriesFormatted() {
    return this.iabCategories.length > 0 ? this.iabCategories.join(', ') : DASH;
  }

  get isChannelTypeDOOH() {
    return this.channelType === LineItemChannelType.OOH;
  }

  get isChannelTypeDisplayVideo() {
    return this.channelType === LineItemChannelType.DISPLAY_VIDEO;
  }

  get channelTypeFormatted() {
    return LineItemChannelTypeOptions.options().find((x) => x.value === this.channelType)?.name || DASH;
  }

  get postalCodesFile() {
    return this.lineItemPostalCodesFiles.map((x) => x.fileName).join(', ');
  }

  get frequencyCapExposureCount(): number {
    const prop = this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_COUNT);
    return prop?.value ? parseInt(prop.value, 10) : null;
  }

  set frequencyCapExposureCount(value: number) {
    let prop = this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_COUNT);
    if (prop == null) {
      prop = new LineItemProperty(-1, this.id, LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_COUNT, `${value}`);
      this.lineItemProperties.push(prop);
    } else {
      prop.value = `${value}`;
    }
  }

  get frequencyCapExposurePerUnit(): FrequencyCapUnit {
    const prop = this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_PER_UNIT);
    return prop?.value ? (parseInt(prop.value, 10) as FrequencyCapUnit) : null;
  }

  set frequencyCapExposurePerUnit(value: FrequencyCapUnit) {
    let prop = this.lineItemProperties.find((x) => x.key === LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_PER_UNIT);
    if (prop == null) {
      prop = new LineItemProperty(-1, this.id, LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_PER_UNIT, `${value}`);
      this.lineItemProperties.push(prop);
    } else {
      prop.value = `${value}`;
    }
  }

  get frequencyCapExposurePerUnitCount(): number {
    const prop = this.lineItemProperties.find(
      (x) => x.key === LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_PER_UNIT_COUNT,
    );
    return prop?.value ? parseInt(prop.value, 10) : null;
  }

  set frequencyCapExposurePerUnitCount(value: number) {
    let prop = this.lineItemProperties.find(
      (x) => x.key === LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_PER_UNIT_COUNT,
    );
    if (prop == null) {
      prop = new LineItemProperty(-1, this.id, LineItemPropertyKeys.FREQUENCY_CAP_EXPOSURE_PER_UNIT_COUNT, `${value}`);
      this.lineItemProperties.push(prop);
    } else {
      prop.value = `${value}`;
    }
  }

  get hasMarkup() {
    return this.margins.find((m) => m.base === 'CPM');
  }

  //
  // Methods
  //
  hasDaypartEnabledInTheLastXHours(hours = 24) {
    const startOfWeek = DateTime.local().startOf('week');
    const hoursSinceStartOfWeek = DateTime.local().startOf('hour').diff(startOfWeek, 'hours').toObject().hours;
    const ranges = [
      {
        start: Math.max(0, hoursSinceStartOfWeek - hours),
        end: hoursSinceStartOfWeek,
      },
    ];
    if (hoursSinceStartOfWeek < hours) {
      ranges.push({
        start: this.daypart.length + hoursSinceStartOfWeek - hours,
        end: this.daypart.length,
      });
    }
    return ranges.reduce((mem, x) => mem || this.daypart.slice(x.start, x.end - x.start).includes('1'), false);
  }

  copy() {
    const cpmGross = this.isCPMFromRateCard ? null : this.CPM;
    return LineItem.fromObject({
      id: this.id,
      campaignID: this.campaignID,
      campaign: this.campaign,
      name: this.name,
      uuid: this.uuid,
      startDate: this.startDate,
      endDate: this.endDate,
      notes: this.notes,
      adDuration: this.adDuration,
      minSecondsBetweenPlays: this.minSecondsBetweenPlays,
      budgetMode: this.budgetMode,
      budget: this.budget,
      budgetBackup: this.budgetBackup,
      networkIDs: this.networkIDs,
      networkExclusionIDs: this.networkExclusionIDs,
      locationIDs: this.locationIDs,
      locationExclusionIDs: this.locationExclusionIDs,
      siteIDs: this.siteIDs,
      siteExclusionIDs: this.siteExclusionIDs,
      unitIDs: this.unitIDs,
      unitExclusionIDs: this.unitExclusionIDs,
      unitPackIDs: this.unitPackIDs,
      unitPackExclusionIDs: this.unitPackExclusionIDs,
      geofenceGroupIDs: this.geofenceGroupIDs,
      geofenceGroupExclusionIDs: this.geofenceGroupExclusionIDs,
      marketIDs: this.marketIDs,
      marketExclusionIDs: this.marketExclusionIDs,
      demographicIDs: this.demographicIDs,
      mediaOwnerIDs: this.mediaOwnerIDs,
      mediaTypeIDs: this.mediaTypeIDs,
      mediaTypeExclusionIDs: this.mediaTypeExclusionIDs,
      creativeIDs: this.creativeIDs,
      inventoryTargetingID: this.inventoryTargetingID,
      daypart: this.daypart,
      targetImpressions: this.targetImpressions,
      cpmGross,
      CPM: cpmGross == null ? null : cpmGross,
      cpmNet: this.cpmNet,
      organization: this.organization,
      organizationID: this.organizationID,
      organizationName: this.organizationName,
      priority: this.priority,
      deliveryMode: this.deliveryMode,
      creationOrigin: this.creationOrigin,
      packageInternal: this.packageInternal,
      packageID: this.packageID,
      packageGroupID: this.packageGroupID,
      isCPMFromRateCard: this.isCPMFromRateCard,
      lastStoredDiscountedCPM: this.lastStoredDiscountedCPM,
      purchaseType: this.purchaseType,
      companionGroupName: this.companionGroupName,
      companionOrder: this.companionOrder,
      lineItemDiscounts: this.lineItemDiscounts,
      organizationDSPID: this.organizationDSPID,
      state: this.state,
      runState: this.runState,
      lineItemProperties:
        this.lineItemProperties != null
          ? this.lineItemProperties.filter((x) => x.key !== LineItemPropertyKeys.MOBILE_RETARGETING) // DO NOT DUPLICATE THE MOBILE RETARGETTING PROPERTY
          : this.lineItemProperties,
      externalID: this.externalID,
      lineItemTargetingCriteria: this.lineItemTargetingCriteria,
      exchangeFeeOverrideType: this.exchangeFeeOverrideType,
      exchangeFeeOverrideValue: this.exchangeFeeOverrideValue,
      maxBidCPM: this.maxBidCPM,
      minBidCPM: this.minBidCPM,
      mobileLineItemIDs: this.mobileLineItemIDs,
      isMeasurementRequested: this.isMeasurementRequested,
      attachedMeasurements: this.attachedMeasurements,
      minConcentrationFactor: this.minConcentrationFactor,
      minConcentrationValue: this.minConcentrationValue,
      minPacing: this.minPacing,
      deliveryCurve: this.deliveryCurve,
      networkFrequencyCaps: this.networkFrequencyCaps,
      unitTags: this.unitTags,
      unitTagExclusions: this.unitTagExclusions,
      dailyGoalCap: this.dailyGoalCap,
      hasInfiniteBudget: this.hasInfiniteBudget,
      margins: this.margins,
      cachedPacing: this.cachedPacing,
      creativeFormats: this.creativeFormats,
      creativeFormatExclusions: this.creativeFormatExclusions,
      apiObject: this.apiObject,
      averageActualCadence: this.averageActualCadence,
      averageScheduledCadence: this.averageScheduledCadence,
      cadence: this.cadence,
      cadenceSmoothCatchup: this.cadenceSmoothCatchup,
      createdOn: this.createdOn,
      createdOnUtc: this.createdOnUtc,
      mediaOwnerApprovals: this.mediaOwnerApprovals,
      packageIDs: this.packageIDs,
      languageIDs: this.languageIDs,
      languageExclusionIDs: this.languageExclusionIDs,
      creativeOrientations: this.creativeOrientations,
      creativeOrientationExclusions: this.creativeOrientationExclusions,
      iabCategories: this.iabCategories,
      iabCategoryExclusions: this.iabCategoryExclusions,
      entityFees: this.entityFees,
      buyFromDealsEnabled: this.buyFromDealsEnabled,
      advertiserBudget: this.advertiserBudget,
      featureKeys: this.featureKeys,
      transientFeatureKeys: this.transientFeatureKeys,
      lineItemPostalCodesFiles: this.lineItemPostalCodesFiles,
      channelType: this.channelType,
      biddingType: this.biddingType,
      isOfflineEnabled: this.isOfflineEnabled,
    });
  }

  clone(name = null, fromLineItemID = null, canUpdateDate = true, isBulkDuplicate = false) {
    const lineItem = this.copy();
    lineItem.id = -1;
    if (name) {
      lineItem.name = name;
    }
    lineItem.uuid = null;
    lineItem.externalID = null; // DO NOT ALLOW TO DUPLICATE EXTERNAL ID !!!
    lineItem.state = LineItemStatus.DRAFT;
    lineItem.runState = RunState.PLAYING; // Force play
    lineItem.lineItemProperties.forEach((x: LineItemProperty) => {
      x.lineItemID = undefined;
      x.id = -1;
    });
    if (this.purchaseType === PurchaseTypes.CADENCE) {
      lineItem.cadence = this.cadence;
      lineItem.cadenceSmoothCatchup = this.cadenceSmoothCatchup;
    }
    //
    // Start date should not be in the past.
    //
    const today = DateTime.local().startOf('day');
    if (canUpdateDate) {
      if (lineItem.startDate < today) {
        lineItem.startDate = today;
        if (lineItem.endDate < today) {
          lineItem.endDate = today;
        }
      }
    }

    lineItem.fromLineItemID = fromLineItemID;
    lineItem.fromCampaignID = this.campaignID;
    lineItem.canDuplicateCreatives = true;
    lineItem.flightRatio = this.flightRatio;

    // Do not override when bulk duplicate
    if (!isBulkDuplicate) {
      lineItem.packageGroupID = null; // DO NOT COPY PACKAGEGROUP ID
      // Do not copy packageIDs when there is a packageID as this will create an unwanted packagegroup
      lineItem.packageGroupDealsIDs = this.packageID ? null : this.packageIDs;

      // Erase apiObject so originalObject() returns null
      lineItem.apiObject = null;
    }

    return lineItem;
  }

  getExtraValidation(isDemandOrganization: boolean, organizationSettings: MembershipSettings, deal: Package = null) {
    // This prevents opening deal details when logged in as an Exchange.
    // if (!mapOrgAppState().organizationSettings) throw new Exception('organizationSettings are not loaded.');

    if (isDemandOrganization == null) {
      isDemandOrganization = mapOrgAppState()?.organization?.isDemandOrganization || false;
    }
    if (organizationSettings == null) {
      organizationSettings = mapOrgAppState()?.organizationSettings;
    }

    const dspLineItemStartDaysInAdvanceRestriction =
      organizationSettings === null ? 2000 : organizationSettings.dspLineItemMaxStartDaysInAdvance;
    const maxStartDate = isDemandOrganization
      ? DateTime.local().plus({ days: dspLineItemStartDaysInAdvanceRestriction })
      : undefined;

    const extras: Record<string, IExtraValidation> = {};

    //
    // Make sure a CPM is specified in "budget" mode
    //
    if (
      !this.isCPMFromRateCard &&
      !organizationSettings.allowZeroFixedRateCPM &&
      this.CPM === 0 &&
      this.isBudgetModeAmount &&
      this.budget !== null &&
      this.budget !== 0
    ) {
      extras.CPM = {
        message: i18n.global.t('messages.cpmMustBeSpecifiedInBudgetMode'),
        preEvaluated: true,
      };
    }

    //
    // Exchange fee override
    //
    if (this.exchangeFeeOverrideType) {
      if (this.exchangeFeeOverrideType === 'PERCENT') {
        extras.exchangeFeeOverrideValue = {
          value: this.exchangeFeeOverrideValue,
          rules: 'required|min_value:0|max_value:0.99',
        };
      }
      if (this.exchangeFeeOverrideType === 'FIXED') {
        extras.exchangeFeeOverrideValue = {
          value: this.exchangeFeeOverrideValue,
          rules: 'required|min_value:0',
        };
      }
    }

    //
    // Budget
    //
    if (this.budget != null && !Number.isNaN(this.budget)) {
      if (this.cpmCalculator.budgetFormatted.includes(' – ')) {
        extras.budget = {
          value: parseInt(this.cpmCalculator.budgetFormatted.split(' – ')[0].split('.')[0].replace(/\D/g, ''), 10),
          rules: 'max_value:2000000000',
        };
      } else {
        extras.budget = {
          value: this.budget,
          rules: 'max_value:2000000000',
        };
      }
    }

    //
    // Advertiser spend
    //
    if (this.advertiserBudget !== null) {
      extras.advertiserBudget = {
        value: this.advertiserBudget,
        rules: 'numeric:float|max_value:2000000000',
        message: capitalize(i18n.global.t('messages.invalidAdvertiserBudget')),
      };
    }

    //
    // Impressions
    //
    if (this.targetImpressions != null) {
      extras.targetImpressions = {
        value: this.targetImpressions,
        rules: 'max_value:2000000000',
      };
    }

    //
    // Infinite budget
    //
    if (!this.hasInfiniteBudget && !this.campaign.isCampaignFlightsEnabled) {
      if (this.isBudgetModeAmount && this.purchaseType !== PurchaseTypes.HOUSE_AD) {
        if (extras.budget != null) {
          extras.budget.rules = `${extras.budget.rules}|greater_than:0|required`;
        } else {
          extras.budget = {
            value: this.budget,
            rules: 'greater_than:0|required',
          };
        }
      } else if (this.isBudgetModeImpressions && this.purchaseType !== PurchaseTypes.HOUSE_AD) {
        if (extras.targetImpressions != null) {
          extras.targetImpressions.rules = `${extras.targetImpressions.rules}|greater_than:0|required`;
        } else {
          extras.targetImpressions = {
            value: this.targetImpressions,
            rules: 'greater_than:0|required',
          };
        }
      }
    }

    if (isDemandOrganization && deal == null) {
      extras.startDate = {
        value: this.startDate,
        rules: `before_or_equal:${maxStartDate.toFormat(getDateFormatForLocale(Validator.locale))}`,
      };
    }

    extras.adDuration = {
      value: this.adDuration,
      rules: 'numeric:int|min_value:0|max_value:180',
    };

    if (
      this.hasDurationRestriction &&
      this.endDate != null &&
      this.startDate != null &&
      this.endDate.diff(this.startDate, 'days').toObject().days > LineItem.MaxLineItemDurationInDays
    ) {
      extras.endDate = {
        message: i18n.global.t('messages.lineItemDurationCannotExceedMaxDays', [LineItem.MaxLineItemDurationInDays]),
        preEvaluated: true,
      };
    }

    //
    // Validate against package restrictions
    //
    if (deal != null && this.creationOrigin !== 'auto') {
      if (!deal.isNGOrPreferredDeal) {
        extras.startDate = {
          value: this.startDate,
          rules: `after_or_equal:${DateTime.fromISO(toServerDate(deal.startDate)).toFormat(
            getDateFormatForLocale(Validator.locale),
          )}`,
        };
      }

      // AD DURATION
      if (!this.isFromPreferredOrNGInternalDeal && !deal.adDurationOverridable) {
        const rules = [];
        if (deal.adDurationMin !== null) {
          rules.push(`min_value:${deal.adDurationMin}`);
        }
        if (deal.adDurationMax !== null) {
          rules.push(`max_value:${deal.adDurationMax}`);
        }
        extras.adDuration = {
          value: this.adDuration,
          rules: Validator.combineRules(rules),
        };
      }

      // BUDGET
      const budgetRules = [];
      if (deal.budgetMin != null) {
        budgetRules.push(`min_value:${deal.budgetMin}`);
      }
      if (deal.budgetMax != null) {
        budgetRules.push(`max_value:${deal.budgetMax}`);
      }
      extras.budget = {
        value: this.budget,
        rules: Validator.combineRules(budgetRules),
      };

      // IMPRESSIONS
      const impressionsRules = [];
      if (deal.marketTargetedImpressionsMin != null) {
        impressionsRules.push(`min_value:${deal.marketTargetedImpressionsMin}`);
      }
      if (deal.marketTargetedImpressionsMax != null) {
        impressionsRules.push(`max_value:${deal.marketTargetedImpressionsMax}`);
      }
      extras.targetImpressions = {
        value: this.targetImpressions,
        rules: Validator.combineRules(impressionsRules),
      };
    }

    //
    // Discounts
    //
    if (this.lineItemDiscounts !== null && this.lineItemDiscounts !== undefined && this.lineItemDiscounts.length > 0) {
      // Check if discount type is greater than 0 and less than or equal to 100%.
      if (this.lineItemDiscounts.filter((x) => x.value <= 0 || x.value > 1).length > 0) {
        extras.lineItemDiscounts = {
          message: i18n.global.t('messages.lineItemDiscountMustBeGreaterThanZeroAndLessThanOrEqualToOneHundred'),
          preEvaluated: true,
        };
      }
      // Check if discount type was used more than once.
      const groupedByDiscountType = this.lineItemDiscounts.reduce((mem, x) => {
        const mutatedMem = { ...mem };
        if (mutatedMem[x.discountType.id]) {
          mutatedMem[x.discountType.id].push(x);
        } else {
          mutatedMem[x.discountType.id] = [x];
        }
        return mutatedMem;
      }, {});
      if (Object.keys(groupedByDiscountType).filter((x) => groupedByDiscountType[x].length > 1).length > 0) {
        extras.lineItemDiscounts = {
          message: i18n.global.t('messages.lineItemCanOnlyHaveOneDiscountPerDiscountType'),
          preEvaluated: true,
        };
      }

      if (this.isBudgetModeAmount) {
        if (this.lineItemDiscounts.filter((d) => d.value === 1).length > 0) {
          extras.lineItemDiscounts = {
            message: i18n.global.t('messages.lineItemDiscountLessThanOneHundredIfBudgetMode'),
            preEvaluated: true,
          };
        }
      }
    }

    //
    // Entity Fees
    //
    if (this.entityFees?.length) {
      const entityFeeValidation = EntityFee.validation(this.entityFees);
      if (entityFeeValidation) {
        extras.lineItemFees = entityFeeValidation;
      }
    }

    //
    // Media Owners
    //
    if (this.isChannelTypeDOOH && this.mediaOwnerIDs?.length === 0 && this.packageGroupDealsIDs == null) {
      extras.lineItemMediaOwnerIDs = {
        message: i18n.global.t('messages.lineItemMediaOwnersMustBeGreaterThanZero'),
        preEvaluated: true,
      };
    }

    //
    // Changing isMeasurementRequested from 'true' to 'false' is not allowed if impressions were delivered.
    //
    if (
      !this.isMeasurementRequested &&
      this.apiObject != null &&
      this.apiObject.measurement_requested &&
      this.hasDeliveredImpressions
    ) {
      extras.isMeasurementRequested = {
        message: i18n.global.t('messages.lineItemMeasurementRequestCannotBeDisabledWhenImpressionsWereDelivered'),
        preEvaluated: true,
      };
    }

    //
    // Make sure at least one network is selected whenever the setting to force its selection is on.
    //
    if (
      organizationSettings.uiForceNetworkSelectionInLineitemsAndDeals &&
      this.networkIDs.length === 0 &&
      this.purchaseType !== PurchaseTypes.HOUSE_AD
    ) {
      extras.networkIDs = {
        message: i18n.global.t('messages.youMustSelectAMininumOfOneNetwork'),
        preEvaluated: true,
      };
    }

    //
    // Cadence
    //
    if (this.purchaseType === PurchaseTypes.CADENCE) {
      extras.cadence = {
        value: this.cadence,
        rules: 'required|numeric:float|min_value:0.01|max_value:9999.0',
      };
    }

    //
    // Budget cap
    //
    if (this.campaign?.budgetCap != null) {
      if (this.sumOfLineItemBudgets > this.campaign.budgetCap) {
        extras.budget = {
          message: i18n.global.t('messages.theLineItemBudgetExceedsTheRemainderOfTheCampaignBudgetCap'),
          preEvaluated: true,
        };
      }
    }

    if (organizationSettings.uiMakeMaxBidRequired && this.maxBidCPM == null) {
      extras.maxBidCPM = {
        message: i18n.global.t('messages.maxBidCPMIsRequired'),
        preEvaluated: true,
      };
    }

    //
    // Companion ad
    //
    if (this.companionOrder != null) {
      extras.companionGroupName = {
        value: this.companionGroupName,
        rules: 'required',
      };
    }

    //
    // Minimum Targeted Impressions
    //
    if (
      !this.hasInfiniteBudget &&
      isDemandOrganization &&
      organizationSettings.lineItemMinimumTargetedImpressions != null
    ) {
      if (
        (this.isBudgetModeImpressions &&
          this.targetImpressions != null &&
          this.targetImpressions < parseInt(organizationSettings.lineItemMinimumTargetedImpressions, 10)) ||
        (this.isBudgetModeAmount &&
          this.cpmCalculator != null &&
          this.cpmCalculator.estimatedTargetImpressions != null &&
          this.cpmCalculator.estimatedTargetImpressions <
            parseInt(organizationSettings.lineItemMinimumTargetedImpressions, 10))
      ) {
        extras.minimumTargetedImpressions = {
          message: i18n.global.t('messages.lineItemTargetedImpressionsTooLow', [
            i18n.global.n(parseInt(organizationSettings.lineItemMinimumTargetedImpressions, 10)),
          ]),
          preEvaluated: true,
        };
      }
    }

    //
    // Pacing Strategy
    //
    if (
      (this.isHouseAdEnabled && this.deliveryCurve !== PacingStrategyEnum.AUDIENCE) ||
      (this.hasCadencePurchaseType && this.isBudgetModeAmount && this.deliveryCurve !== PacingStrategyEnum.LINEAR)
    ) {
      extras.deliveryCurve = {
        message: i18n.global.t('messages.cadenceAndHouseAdsPurchasTypesCannotHaveAPacingStrategy'),
        preEvaluated: true,
      };
    }

    if (this.isNew && this.startDate < DateTime.local().startOf('day')) {
      extras.startDate = {
        message: i18n.global.t('messages.lineItemStartDateCannotBeSetInThePast'),
        preEvaluated: true,
      };
    } else if (!this.isDuplicateLineItem) {
      //
      // Start date cannot be modified after impressions have been delivered.
      //
      if (!this.isStartDateEditable && this.toApi().flight_from !== this.apiObject?.flight_from) {
        extras.startDate = {
          message: i18n.global.t('messages.lineItemStartDateCannotBeChangedAfterImpressionsAreDelivered'),
          preEvaluated: true,
        };
      }
      if (!this.isEndDateEditable && this.toApi().flight_to !== this.apiObject?.flight_to) {
        extras.endDate = {
          message: i18n.global.t('messages.lineItemEndDateCannotBeChanged'),
          preEvaluated: true,
        };
      }
    }

    //
    // Display / Video
    //
    if (this.isChannelTypeDisplayVideo) {
      const dealIDs = this.dealIDsBySspProperty?.value ? JSON.parse(this.dealIDsBySspProperty.value) : {};
      if (Object.entries(dealIDs).length === 0 || Object.values(dealIDs).some((x) => x == null || x == '')) {
        // Make sure a deal has been entered.
        extras.dealIDs = {
          message: i18n.global.t('messages.displayAndVideoLineItemsMustHaveADeal'),
          preEvaluated: true,
        };
      }

      // Fixed bid CPM is set by adding the same value to both min and max bid CPM.
      if (
        this.biddingType === BiddingType.Fixed &&
        (!this.minBidCPM || !this.maxBidCPM || this.minBidCPM !== this.maxBidCPM)
      ) {
        extras.fixedBidCPM = {
          message: i18n.global.t('messages.fixedBidCpmRequired'),
          preEvaluated: true,
        };
      }
    }

    // Fixed bidding price and curated deals are not compatible
    if (this.biddingType === BiddingType.Fixed && this.isFromCuratedDeal) {
      extras.fixedBidCPM = {
        message: i18n.global.t('messages.fixedBiddingStrategyCuratedDealsNotCompatible'),
        preEvaluated: true,
      };
    }

    //
    // Daypart
    //
    if (this.isDaypartEmpty) {
      extras.daypart = {
        message: i18n.global.t('messages.daypartNotEmpty'),
        preEvaluated: true,
      };
    }

    return extras;
  }

  async getPublishingWarnings(isDemandOrganization: boolean): Promise<string[]> {
    const warnings = [];
    //
    // As a demand org, never buy Stroeer on open exchange.
    //
    const mediaOwners = await runQueryInScope(useMediaOwners);
    if (isDemandOrganization && this.packageID == null && mediaOwners.length > 0) {
      const mediaOwnerNames = [
        // 'Ströer', CAM-2351 - According to Ronan Cezar, it should be safe to remove. Commenting it in case it needs to be reverted.
        'Goldbach',
      ];
      mediaOwnerNames.forEach((mediaOwnerName) => {
        const mediaOwner = mediaOwners.filter((x: MediaOwner) => x.name.toLowerCase() === mediaOwnerName.toLowerCase());
        if (this.mediaOwnerIDs.some((x) => mediaOwner.find((s: MediaOwner) => s.id === x))) {
          warnings.push(
            `${i18n.global.t('messages.youAreTargetingAMediaOwnerThatCannotBeBoughtViaTheOpenExchange')}: ${mediaOwnerName}`,
          );
        }
      });
    }

    //
    // When Buy From Deals is enabled, a deal need to be bought or disable the section
    //
    if (
      this.buyFromDealsEnabled &&
      isDemandOrganization &&
      !this.packageID &&
      !this.packageGroupID &&
      !this.packageGroupDealsIDs?.length &&
      !this.packageIDs?.length
    ) {
      warnings.push(i18n.global.t('messages.youNeedToSelectOneDealOrYouCanDisableIt'));
    }
    return warnings;
  }

  copyInventoryTargetingFrom(lineItem: LineItem) {
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_LOCATIONIDS}`, {
      lineItem: this,
      value: [...lineItem.locationIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_LOCATIONEXCLUSIONIDS}`, {
      lineItem: this,
      value: [...lineItem.locationExclusionIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_MARKET_IDS}`, {
      lineItem: this,
      value: [...lineItem.marketIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_MARKETEXCLUSIONIDS}`, {
      lineItem: this,
      value: [...lineItem.marketExclusionIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_NETWORKIDS}`, {
      lineItem: this,
      value: [...lineItem.networkIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_NETWORKEXCLUSIONIDS}`, {
      lineItem: this,
      value: [...lineItem.networkExclusionIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_GEOFENCE_GROUP_IDS}`, {
      lineItem: this,
      value: [...lineItem.geofenceGroupIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_GEOFENCE_GROUP_EXCLUSION_IDS}`, {
      lineItem: this,
      value: [...lineItem.geofenceGroupExclusionIDs],
    });
    rootStore.commit(modelMutationTypes.UPDATE_LINEITEM_LANGUAGEIDS, {
      lineItem: this,
      value: [...lineItem.languageIDs],
    });
    rootStore.commit(modelMutationTypes.UPDATE_LINEITEM_LANGUAGEEXCLUSIONIDS, {
      lineItem: this,
      value: [...lineItem.languageExclusionIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_MEDIATYPEIDS}`, {
      lineItem: this,
      value: [...lineItem.mediaTypeIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_MEDIATYPEEXCLUSIONIDS}`, {
      lineItem: this,
      value: [...lineItem.mediaTypeExclusionIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_UNITIDS}`, {
      lineItem: this,
      value: [...lineItem.unitIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_UNITEXCLUSIONIDS}`, {
      lineItem: this,
      value: [...lineItem.unitExclusionIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_SITEIDS}`, {
      lineItem: this,
      value: [...lineItem.siteIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_SITEEXCLUSIONIDS}`, {
      lineItem: this,
      value: [...lineItem.siteExclusionIDs],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_CREATIVEFORMATS}`, {
      lineItem: this,
      value: [...lineItem.creativeFormats],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_CREATIVEFORMATEXCLUSIONS}`, {
      lineItem: this,
      value: [...lineItem.creativeFormatExclusions],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_CREATIVEORIENTATIONS}`, {
      lineItem: this,
      value: [...lineItem.creativeOrientations],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_CREATIVEORIENTATIONEXCLUSIONS}`, {
      lineItem: this,
      value: [...lineItem.creativeOrientationExclusions],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_IABCATEGORIES}`, {
      lineItem: this,
      value: [...lineItem.iabCategories],
    });
    rootStore.commit(`${modelMutationTypes.UPDATE_LINEITEM_IABCATEGORYEXCLUSIONS}`, {
      lineItem: this,
      value: [...lineItem.iabCategoryExclusions],
    });
  }

  hasFeature(key: string) {
    return this.transientFeatureKeys.includes(key) || this.featureKeys.includes(key);
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = [
      'archived',
      'average_actual_cadence',
      'average_cpm',
      'average_scheduled_cadence',
      'cached_impressions_writes_without_refresh',
      'cachedpacing',
      'campaign',
      'confirmed_impressions',
      'cpm',
      'cpp',
      'created_by',
      'created_on_utc',
      'delivery_rate',
      'demographic_ids',
      'ecpm_average',
      'ecpm_max',
      'ecpm_min',
      'features',
      'has_custom_trigger_creative',
      'has_env_trigger_creative',
      'impressions_to_deliver_if_modified',
      'inventorytargeting_id',
      'is_offline_enabled',
      'last_played_time_utc',
      'lineitem_creatives',
      'lineitem_discounts',
      'lineitem_extracosts',
      'lineitem_flights',
      'lineitem_postalcodesfiles',
      'lineitem_targetingcriteria',
      'lineitem_unitassignments',
      'lineitem_unitsiteexclusions',
      'lineitem_properties',
      'mediaowner_approvals',
      'mobilelineitem_ids',
      'modified_by',
      'modified_on_utc',
      'organization',
      'pacing',
      'pacing_period_date_if_modified',
      'pacing_period_start_spend',
      'package',
      'progress',
      'scheduled_impressions',
      'submitted_at',
      'targeted_impressions_per_unit',
      'total_impressions',
      'total_spend',
    ];

    if (this.budgetMode === BudgetModes.IMPRESSIONS) ignoreProperties.push('budget');
    else ignoreProperties.push('market_targeted_impressions');

    if (this.purchaseType === PurchaseTypes.HOUSE_AD) {
      ignoreProperties.push('budget', 'cpm_gross', 'minimum_pacing');
    }

    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);
    const discountDiffs = getArrayDiff(this.apiObject.lineitem_discounts, this.toApi().lineitem_discounts, [
      'created_on_utc',
      'modified_on_utc',
      'discounttype',
    ]);
    const demographicDiffs = getArrayDiff(this.apiObject.demographic_ids, this.toApi().demographic_ids);
    const prepareLineTargetingCriteria = (apiObject: ILineItemAPI) => {
      return apiObject.lineitem_targetingcriteria.map((x) => ({
        targetingcriterion_id: x.targetingcriterion_id,
        lineitemtargetingcriterion_values: x.lineitemtargetingcriterion_values.map((y) => ({
          code: y.code,
          value: y.value,
        })),
      }));
    };
    const targetingCriteriaDiffs = getDiff(
      prepareLineTargetingCriteria(this.apiObject),
      prepareLineTargetingCriteria(this.toApi()),
    );

    if (
      diffs.length === 0 &&
      demographicDiffs.length === 0 &&
      discountDiffs.length === 0 &&
      targetingCriteriaDiffs.length === 0
    )
      return true;
    DatadogApi.logUnexpectedChange(diffs);
    return false;
  }

  async getChanges(): Promise<IEntityChange<LineItem>[]> {
    if (this.isNew) return [];

    return this.getValidatableChanges<LineItem>(this.originalObject, LineItem.entityProperties, [
      this.budgetMode === BudgetModes.BUDGET ? 'targetImpressions' : 'budget',
      'attachedMeasurements',
      'audienceFile',
    ]);
  }

  static fromObject(obj: ILineItem) {
    return super.fromObject(obj, new LineItem());
  }

  static fromApi(apiObject) {
    const organization =
      apiObject.organization === undefined
        ? {
            organization_id: null,
            name: null,
            logo_url: null,
          }
        : apiObject.organization;

    const lineItemProperties =
      apiObject.lineitem_properties !== undefined
        ? apiObject.lineitem_properties
            .filter((x) => x.organization_id == null || x.organization_id === mapOrgAppState().organization.id)
            .map((x) => LineItemProperty.fromApi(x))
        : [];
    const obj: ILineItem = {
      apiObject,
      id: apiObject.lineitem_id,
      channelType: apiObject.channel_type,
      campaignID: apiObject.campaign_id,
      campaign: apiObject.campaign !== undefined ? Campaign.fromApi(apiObject.campaign) : new Campaign(),
      name: apiObject.name,
      uuid: apiObject.uuid,
      startDate: DateTime.fromISO(apiObject.flight_from),
      endDate: DateTime.fromISO(apiObject.flight_to),
      notes: apiObject.notes,
      adDuration: apiObject.ad_duration,
      minSecondsBetweenPlays: apiObject.min_seconds_between_plays,
      budgetMode: apiObject.budget_mode,
      budget: apiObject.budget,
      budgetBackup: apiObject.budget,
      demographicIDs: apiObject.demographic_ids,
      creativeIDs: apiObject.creative_ids,
      inventoryTargetingID: apiObject.inventory_targeting?.inventorytargeting_id || -1,
      creativeOrientations: apiObject.inventory_targeting?.creative_orientations || [],
      creativeOrientationExclusions: apiObject.inventory_targeting?.creative_orientation_exclusions || [],
      iabCategories: apiObject.inventory_targeting?.iab_categories || [],
      iabCategoryExclusions: apiObject.inventory_targeting?.iab_category_exclusions || [],
      languageIDs: apiObject.inventory_targeting?.language_ids || [],
      languageExclusionIDs: apiObject.inventory_targeting?.language_exclusion_ids || [],
      locationIDs: apiObject.inventory_targeting?.location_ids || [],
      locationExclusionIDs: apiObject.inventory_targeting?.location_exclusion_ids || [],
      geofenceGroupIDs: apiObject.inventory_targeting?.geofencegroup_ids || [],
      geofenceGroupExclusionIDs: apiObject.inventory_targeting?.geofencegroup_exclusion_ids || [],
      marketIDs: apiObject.inventory_targeting?.market_ids || [],
      marketExclusionIDs: apiObject.inventory_targeting?.market_exclusion_ids || [],
      mediaTypeIDs: apiObject.inventory_targeting?.mediatype_ids,
      mediaTypeExclusionIDs: apiObject.inventory_targeting?.mediatype_exclusion_ids || [],
      networkIDs: apiObject.inventory_targeting?.network_ids || [],
      networkExclusionIDs: apiObject.inventory_targeting?.network_exclusion_ids || [],
      siteIDs: apiObject.inventory_targeting?.site_ids || [],
      siteExclusionIDs: apiObject.inventory_targeting?.site_exclusion_ids || [],
      unitIDs: apiObject.inventory_targeting?.unit_ids || [],
      unitExclusionIDs: apiObject.inventory_targeting?.unit_exclusion_ids || [],
      unitPackIDs: apiObject.inventory_targeting?.unitpack_ids || [],
      unitPackExclusionIDs: apiObject.inventory_targeting?.unitpack_exclusion_ids || [],
      mediaOwnerIDs: apiObject.inventory_targeting?.owner_ids,
      co2eEmissionsScoreThreshold: apiObject.inventory_targeting?.co2e_emissions_score_threshold,
      daypart: apiObject.daypart,
      targetImpressions: apiObject.market_targeted_impressions,
      state: apiObject.state,
      createdOn: DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }),
      submittedOn: apiObject.submitted_at != null ? DateTime.fromISO(apiObject.submitted_at) : null,
      approvedOn: apiObject.approved_on != null ? DateTime.fromISO(apiObject.approved_on) : null,
      approvalComment: apiObject.approval_comment,
      cancelledOn: apiObject.cancelled_at != null ? DateTime.fromISO(apiObject.cancelled_at) : null,
      mediaOwnerApprovals:
        apiObject.mediaowner_approvals !== undefined
          ? apiObject.mediaowner_approvals.map((x) => MediaOwnerApproval.fromApi(x))
          : [],
      organization: organization.organization_id == null ? organization : Organization.fromApi(organization),
      organizationID: organization.organization_id,
      organizationName: organization.name != null ? Organization.fromApi(organization).name : null,
      priority: apiObject.priority !== undefined ? apiObject.priority : 5,
      deliveryMode: apiObject.delivery_mode,
      creationOrigin: apiObject.creation_origin,
      packageID: apiObject.package_id,
      packageInternal:
        apiObject.package !== undefined && apiObject.package !== null ? Package.fromApi(apiObject.package) : null,
      packageGroupID: apiObject.packagegroup_id,
      isCPMFromRateCard: apiObject.is_cpm_from_rate_card,
      lastStoredDiscountedCPM: apiObject.cpm,
      CPM: apiObject.cpm,
      purchaseType: apiObject.purchase_type,
      companionGroupName: apiObject.companion_group_name,
      companionOrder: apiObject.companion_order,
      lineItemDiscounts:
        apiObject.lineitem_discounts !== undefined
          ? apiObject.lineitem_discounts.map((x) => LineItemDiscount.fromApi(x))
          : [],
      organizationDSPID: apiObject.organization_dsp_id,
      runState: apiObject.run_state,
      lineItemProperties,
      externalID: apiObject.external_id,
      lineItemTargetingCriteria:
        apiObject.lineitem_targetingcriteria != null
          ? apiObject.lineitem_targetingcriteria.map((x) => LineItemTargetingCriterion.fromApi(x))
          : [],
      exchangeFeeOverrideMinimum: apiObject.exchange_fee_override_minimum,
      exchangeFeeOverrideType: apiObject.exchange_fee_override_type,
      exchangeFeeOverrideValue: apiObject.exchange_fee_override_value,
      lastPlayedTime:
        apiObject.last_played_time_utc != null
          ? DateTime.fromSeconds(apiObject.last_played_time_utc)
          : apiObject.last_played_time_utc,
      cachedPacing: apiObject.cachedpacing != null ? CachedPacing.fromApi(apiObject.cachedpacing) : null,
      createdOnUtc:
        apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      modifiedOnUtc:
        apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      maxBidCPM: apiObject.max_bid_cpm,
      minBidCPM: apiObject.min_bid_cpm,
      mobileLineItemIDs: apiObject.mobilelineitem_ids || [],
      isMeasurementRequested: apiObject.measurement_requested,
      attachedMeasurements: apiObject.measurements?.map((x) => Measurement.fromApi(x)) ?? [],
      minConcentrationFactor: apiObject.min_concentration_factor,
      minConcentrationValue: apiObject.min_concentration_value,
      minPacing: apiObject.minimum_pacing,
      deliveryCurve: apiObject.delivery_curve,
      networkFrequencyCaps: apiObject.network_frequency_caps?.map((x) => NetworkFrequencyCap.fromApi(x)),
      cadence: apiObject.cadence,
      averageActualCadence: apiObject.average_actual_cadence,
      averageScheduledCadence: apiObject.average_scheduled_cadence,
      unitTags: apiObject.unit_tags,
      unitTagExclusions: apiObject.unit_tag_exclusions,
      dailyGoalCap: apiObject.cap,
      cadenceSmoothCatchup: apiObject.cadence_smooth_catchup,
      hasInfiniteBudget: apiObject.infinite_budget,
      margins: apiObject.lineitem_margins != null ? apiObject.lineitem_margins.map((x) => MarginValue.fromApi(x)) : [],
      creativeFormats: apiObject.creative_formats,
      creativeFormatExclusions: apiObject.creative_format_exclusions,
      cpp: apiObject.cpp,
      packageIDs: apiObject.package_ids,
      cpmNet: null,
      flightRatio: apiObject.flight_goal_ratio ? apiObject.flight_goal_ratio * 100 : null,
      entityFees: apiObject.entity_fees?.map((f) => EntityFee.fromApi(f)) ?? null,
      buyFromDealsEnabled: apiObject.buy_from_deals_enabled,
      advertiserBudget: apiObject.advertiser_budget,
      featureKeys: apiObject.features ?? [],
      transientFeatureKeys: [],
      lineItemPostalCodesFiles:
        apiObject.lineitem_postalcodesfiles != null
          ? apiObject.lineitem_postalcodesfiles.map((x) => PostalCodesFile.fromApi(x.postal_codes_file))
          : [],
      package: apiObject.package ? Package.fromApi(apiObject.package) : null,
      biddingType: apiObject.bidding_type,
      isOfflineEnabled: apiObject.is_offline_enabled,
      //
      // UPDATE THE CLONE METHOD IF YOU ADD NEW PROPERTIES HERE !!!
      //
    };

    if (obj.lineItemDiscounts?.length > 0) {
      obj.CPM = obj.isCPMFromRateCard ? null : apiObject.cpm_gross;
    }

    const lineItem = LineItem.fromObject(obj);

    if (apiObject.lineitem_flights) {
      lineItem.flights = apiObject.lineitem_flights.map((x) => Flight.fromApi(x, null, lineItem));
    }

    lineItem.apiObject = apiObject;

    return lineItem;
  }

  toBulkUpdateApi() {
    const apiObject = this.toApi();
    const newObj = {
      name: apiObject.name,
      flight_from: apiObject.flight_from,
      flight_to: apiObject.flight_to,
      notes: apiObject.notes,
      budget_mode: apiObject.budget_mode,
      budget: apiObject.budget,
      demographic_ids: apiObject.demographic_ids,
      creative_ids: apiObject.creative_ids,
      daypart: apiObject.daypart,
      lineitem_properties: apiObject.lineitem_properties, // We do not support editing deal id / ssp
      max_bid_cpm: apiObject.max_bid_cpm,
      delivery_curve: apiObject.delivery_curve,
      cap: apiObject.cap,
      market_targeted_impressions: apiObject.market_targeted_impressions,
      priority: apiObject.priority,
      purchase_type: apiObject.purchase_type,
      cpm_gross: apiObject.cpm_gross,
      is_cpm_from_rate_card: apiObject.is_cpm_from_rate_card,
      min_seconds_between_plays: apiObject.min_seconds_between_plays,
      unit_ids: apiObject.unit_ids,
      site_ids: apiObject.site_ids,
      infinite_budget: apiObject.infinite_budget,
      mediaowner_ids: apiObject.mediaowner_ids,
    };

    if (apiObject.purchase_type === PurchaseTypes.CADENCE) {
      newObj.cadence = apiObject.cadence;
      newObj.cadence_smooth_catchup = apiObject.cadence_smooth_catchup;
    }

    return newObj;
  }

  toApi(): ILineItemAPI {
    const lineItemProperties = [...this.lineItemProperties];

    const apiObject: ILineItemAPI = {
      lineitem_id: this.id >= 0 ? this.id : undefined,
      channel_type: this.channelType,
      uuid: this.uuid,
      name: this.name,
      flight_from: toServerDate(this.startDate),
      flight_to: toServerDate(this.endDate),
      notes: this.notes,
      ad_duration:
        this.adDuration === 0 || this.adDuration === undefined || this.adDuration === null
          ? 0
          : parseInt(`${this.adDuration}`, 10),
      min_seconds_between_plays: parseInt(`${this.minSecondsBetweenPlays}`, 10),
      cpm_gross: this.isCPMFromRateCard ? null : this.CPM,
      budget_mode: this.budgetMode !== null ? this.budgetMode : BudgetModes.IMPRESSIONS,
      budget: this.budget !== null && this.isBudgetModeAmount ? parseFloat(`${this.budget}`) : null,
      advertiser_budget: this.advertiserBudget,
      campaign_id: this.campaignID,
      network_ids: this.networkIDs,
      network_exclusion_ids: this.networkExclusionIDs,
      location_ids: this.locationIDs,
      location_exclusion_ids: this.locationExclusionIDs,
      site_ids: this.siteIDs,
      site_exclusion_ids: this.siteExclusionIDs,
      unit_ids: this.unitIDs,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_tags: this.unitTags,
      unit_tag_exclusions: this.unitTagExclusions,
      unitpack_ids: this.unitPackIDs,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      market_ids: this.marketIDs,
      demographic_ids: this.demographicIDs,
      mediaowner_ids: this.mediaOwnerIDs,
      mediatype_ids: this.mediaTypeIDs,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      creative_ids: this.creativeIDs,
      daypart: this.daypart,
      market_targeted_impressions: this.isBudgetModeImpressions ? parseInt(`${this.targetImpressions}`, 10) : null,
      state: this.state === 'A' || this.state === 'P' ? 'S' : this.state, // State can no longer be 'A'
      created_at: toServerDateTime(this.createdOn),
      submitted_at: this.submittedOn != null ? toServerDateTimeNoTimezone(this.submittedOn) : null,
      cancelled_at: toServerDateTimeNoTimezone(this.cancelledOn, true),
      delivery_mode: this.deliveryMode,
      creation_origin: this.creationOrigin,
      package_id: this.packageID,
      package_ids: this.packageIDs,
      packagegroup_id: this.packageGroupID,
      purchase_type: this.purchaseType,
      priority: this.priority,
      companion_group_name: this.companionGroupName,
      companion_order: this.companionOrder,
      lineitem_discounts: this.lineItemDiscounts.map((x) => x.toApi()),
      is_cpm_from_rate_card: this.isCPMFromRateCard,
      run_state: this.runState,
      lineitem_properties: lineItemProperties.map((x) => x.toApi()),
      external_id: this.externalID,
      lineitem_targetingcriteria:
        this.lineItemTargetingCriteria != null ? this.lineItemTargetingCriteria.map((x) => x.toApi()) : undefined,
      exchange_fee_override_minimum: this.exchangeFeeOverrideMinimum,
      exchange_fee_override_type: this.exchangeFeeOverrideType,
      exchange_fee_override_value: this.exchangeFeeOverrideValue,
      max_bid_cpm: this.maxBidCPM,
      min_bid_cpm: this.minBidCPM,
      mobilelineitem_ids: this.mobileLineItemIDs,
      measurement_requested: this.isMeasurementRequested,
      measurements: this.attachedMeasurements?.map((x) => x.toApi()),
      min_concentration_factor: this.minConcentrationFactor,
      minimum_pacing: this.minPacing,
      delivery_curve: this.deliveryCurve,
      network_frequency_caps: this.networkFrequencyCaps?.map((x) => x.toApi()) ?? [],
      cap: this.dailyGoalCap,
      organization_dsp_id: this.organizationDSPID,
      infinite_budget: this.hasInfiniteBudget,
      lineitem_margins: this.margins.map((x) => x.toApi()),
      creative_formats: this.creativeFormats,
      creative_format_exclusions: this.creativeFormatExclusions,
      inventory_targeting: this.isChannelTypeDOOH
        ? {
            inventorytargeting_id: this.inventoryTargetingID,
            market_ids: this.marketIDs,
            market_exclusion_ids: this.marketExclusionIDs,
            owner_ids: this.mediaOwnerIDs,
            language_ids: this.languageIDs,
            language_exclusion_ids: this.languageExclusionIDs,
            location_ids: this.locationIDs,
            location_exclusion_ids: this.locationExclusionIDs,
            geofencegroup_ids: this.geofenceGroupIDs,
            geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
            mediatype_ids: this.mediaTypeIDs,
            mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
            network_ids: this.networkIDs,
            network_exclusion_ids: this.networkExclusionIDs,
            site_ids: this.siteIDs,
            site_exclusion_ids: this.siteExclusionIDs,
            unit_ids: this.unitIDs,
            unit_exclusion_ids: this.unitExclusionIDs,
            unit_tags: this.unitTags,
            unit_tag_exclusions: this.unitTagExclusions,
            unitpack_ids: this.unitPackIDs,
            unitpack_exclusion_ids: this.unitPackExclusionIDs,
            creative_formats: this.creativeFormats,
            creative_format_exclusions: this.creativeFormatExclusions,
            creative_orientations: this.creativeOrientations,
            creative_orientation_exclusions: this.creativeOrientationExclusions,
            iab_categories: this.iabCategories,
            iab_category_exclusions: this.iabCategoryExclusions,
            co2e_emissions_score_threshold: this.co2eEmissionsScoreThreshold,
          }
        : ({} as IInventoryTargetingAPI),
      flight_goal_ratio: this.flightGoalRatioPercentage,
      entity_fees: this.entityFees?.map((f) => f.toApi()),
      buy_from_deals_enabled: this.buyFromDealsEnabled,
      postalcodesfile_ids: this.lineItemPostalCodesFiles.map((x) => x.id),
      cadence_smooth_catchup: this.cadenceSmoothCatchup, // For some reason it is required for the display/video channel...
      bidding_type: this.biddingType,
      is_offline_enabled: this.isOfflineEnabled,
      //
      // UPDATE THE CLONE METHOD IF YOU ADD NEW PROPERTIES HERE !!!
      //
    };

    if (!this.entityFees || this.entityFees.length === 0) delete apiObject.entity_fees;
    if (this.lineItemPostalCodesFiles.length === 0) delete apiObject.postalcodesfile_ids;
    if (this.attachedMeasurements.length === 0) delete apiObject.measurements;

    if (this.purchaseType === PurchaseTypes.CADENCE || !this.isNew) {
      apiObject.cadence = this.cadence;
    }

    if (this.minConcentrationValue !== null) {
      apiObject.min_concentration_value = this.minConcentrationValue;
    }

    if (this.isInlineEdited) {
      LineItem.expandedProperties.forEach((property) => delete apiObject[property]);
    }

    if (this.hasInfiniteBudget && this.isFromPreferredOrNGInternalDeal && this.campaign.pacingBudget != null) {
      if (this.budgetMode === BudgetModes.BUDGET && this.campaign.pacingBudgetMode === BudgetModes.BUDGET) {
        apiObject.budget = this.campaign.pacingBudget;
      } else if (
        this.budgetMode === BudgetModes.IMPRESSIONS &&
        this.campaign.pacingBudgetMode === BudgetModes.IMPRESSIONS
      ) {
        apiObject.market_targeted_impressions = this.campaign.pacingBudget;
      }
    }

    if (this.isChannelTypeDisplayVideo) {
      // Set a bunch of required property for display/video channel
      apiObject.is_offline_enabled = false;
      apiObject.delivery_rate = 'regular';
    }

    return apiObject;
  }

  toApiStateChange() {
    return {
      state: this.state,
      approval_comment: this.approvalComment,
    };
  }

  toApiRunStateChange() {
    return {
      run_state: this.runState,
      approval_comment: this.approvalComment,
    };
  }

  static validatorCreateRules(li): Record<string, string> {
    const rules: Record<string, string> = {
      name: 'required|max:191|not_empty_or_whitespace',
      startDate: 'required|date_time|before_or_equal:endDate',
      endDate: 'required|date_time|after_or_equal:startDate',
      mediaOwnerIDs: 'required',
      priority: 'required|numeric:int|min_value:1|max_value:9',
      dailyGoalCap: 'greater_than:0',
      purchaseType: 'required',
    };

    if (!li.isFromPreferredOrNGInternalDeal && !li.isChannelTypeDisplayVideo) {
      rules.minSecondsBetweenPlays = 'required|numeric:int|min_value:0|max_value:3600';
    }

    if (!li.hasInfiniteBudget && !li.isHouseAdEnabled && !li.campaign.isCampaignFlightsEnabled) {
      if (li.isBudgetModeAmount) {
        rules.budget = 'required|numeric:float|greater_than:0';
      } else if (li.isBudgetModeImpressions) {
        rules.targetImpressions = 'required|numeric:int|greater_than:0';
      }
    }
    return rules;
  }

  static get expands() {
    return [
      'cachedpacing',
      'campaign.advertiser.unique_advertiser.replacement_unique_advertiser.advertiser_domains',
      'campaign.margins',
      'inventory_targeting',
      'lineitem_creatives',
      'lineitem_creativeformatexclusions',
      'lineitem_creativeformats',
      'lineitem_discounts/discounttype',
      'lineitem_flights.flight',
      'lineitem_margins',
      'lineitem_marketexclusions',
      'lineitem_mediatypeexclusions',
      'lineitem_networkexclusions',
      'lineitem_networkfreqcaps',
      'lineitem_postalcodesfiles',
      'lineitem_properties',
      'lineitem_targetingcriteria/lineitemtargetingcriterion_values',
      'lineitem_unitpackexclusions',
      'lineitem_unitpacks',
      'package',
      'package_ids',
      'features',
    ];
    //
    // UPDATE EXPANDEDPROPERTIES IF YOU ADD NEW EXPAND QUERY PARAMETER
    //
  }

  static get expandedProperties() {
    return [
      'creative_format_exclusions',
      'creative_formats',
      'inventory_targeting',
      'lineitem_creatives',
      'lineitem_discounts',
      'lineitem_flights.flight',
      'lineitem_margins',
      'lineitem_postalcodesfiles',
      'lineitem_targetingcriteria',
      'market_exclusion_ids',
      'mediatype_exclusion_ids',
      'network_exclusion_ids',
      'network_frequency_caps',
      'unitpack_exclusion_ids',
      'unitpack_ids',
      'features',
    ];
  }

  static get bulkEditProperties() {
    const state = mapOrgAppState();
    let isSupplyOrganization = false;
    let organizationSettings: MembershipSettings[] = null;
    if (state) {
      isSupplyOrganization = state.organization.isSupplyOrganization;
      organizationSettings = state.organizationSettings;
    }
    return [
      ...(isSupplyOrganization || organizationSettings?.uiEnableDemandPriorityManagement
        ? [LineItem.entityProperties.reversedPriority]
        : []),
      ...(isSupplyOrganization ? [LineItem.entityProperties.startDate] : []),
      ...(isSupplyOrganization ? [LineItem.entityProperties.endDate] : []),
      LineItem.entityProperties.budget,
      LineItem.entityProperties.targetImpressions,
      LineItem.entityProperties.minSecondsBetweenPlays,
      LineItem.entityProperties.dailyGoalCap,
      LineItem.entityProperties.maxBidCPM,
      LineItem.entityProperties.deliveryCurve,
      LineItem.entityProperties.audienceFile,
    ];
  }

  static get bulkV4Properties() {
    let properties = [
      LineItem.entityProperties.name,
      LineItem.entityProperties.campaignName,
      LineItem.entityProperties.packageID,
      LineItem.entityProperties.advertiser,
      LineItem.entityProperties.account,
      LineItem.entityProperties.startDate,
      LineItem.entityProperties.endDate,
      LineItem.entityProperties.purchaseType,
      LineItem.entityProperties.budget,
      LineItem.entityProperties.targetImpressions,
      LineItem.entityProperties.dailyGoalCap,
      LineItem.entityProperties.maxBidCPM,
      LineItem.entityProperties.deliveryCurve,
      LineItem.entityProperties.mediaOwners,
      LineItem.entityProperties.mediaTypes,
      LineItem.entityProperties.mediaTypeExclusions,
      LineItem.entityProperties.markets,
      LineItem.entityProperties.marketExclusions,
      LineItem.entityProperties.creativeFormats,
      LineItem.entityProperties.creativeFormatExclusions,
      LineItem.entityProperties.minSecondsBetweenPlays,
      LineItem.entityProperties.audienceFile,
      LineItem.entityProperties.daypart,
      LineItem.entityProperties.notes,
    ];
    let isSupplyOrganization = false;
    if (mapOrgAppState() != null && mapOrgAppState().organization != null) {
      isSupplyOrganization = mapOrgAppState().organization.isSupplyOrganization;

      if (isSupplyOrganization) {
        properties = [...properties, ...this.bulkV4PropertiesForSupplySide];
      }
    }

    return properties;
  }

  static get bulkV4PropertiesForSupplySide() {
    return [
      LineItem.entityProperties.networks,
      LineItem.entityProperties.networkExclusions,
      LineItem.entityProperties.unitPacks,
      LineItem.entityProperties.unitPackExclusions,
      LineItem.entityProperties.reversedPriority,
    ];
  }

  static get bulkV4OptionalProperties() {
    return [LineItem.entityProperties.packageID, ...this.bulkV4PropertiesForSupplySide];
  }

  static get entityProperties() {
    if (LineItem.entityPropertiesCached != null) {
      return LineItem.entityPropertiesCached;
    }
    LineItem.entityPropertiesCached = getEntityProperties();
    return LineItem.entityPropertiesCached;
  }

  static get entityName(): string {
    return type;
  }

  static async getEntityIDsFromModels(models) {
    try {
      const getLineItemsAction = ({ queryParams, cancelTokenSource, showLoader }) =>
        rootStore.dispatch('orgApp/getLineItems', { queryParams, cancelTokenSource, showLoader });
      const filterGroups = models.map(
        (x) =>
          new FilterGroup(
            [new Filter('name', [x.name]), new Filter('campaign.name', [x.campaign.name])],
            FilterLogicalOperator.AND,
          ),
      );
      const filterCollection = new FilterCollection(filterGroups, FilterLogicalOperator.OR);
      const qp = QueryParams.all(filterCollection);
      qp.include = ['lineitem_id', 'name'];
      qp.expand = ['campaign'];
      const response = await qp.run(getLineItemsAction);
      const entities = response.items;
      return models.map((model) => ({
        model,
        id: entities.find((x) => x.name === model.name && x.campaign.name === model.campaign.name)?.id,
      }));
    } catch (e) {
      throw new Exception('Failed to get line item IDs.');
    }
  }

  static async assignIDs(entities) {
    if (entities.length === 0) return [];

    //
    // Campaigns
    //
    const existingCampaignIDMapping = await Campaign.getEntityIDsFromModels(entities.map((x) => x.campaign));
    entities.forEach((x) => {
      x.campaignID = existingCampaignIDMapping.find((y) => y.model.name === x.campaign.name)?.id || -1;

      x.campaign.id = existingCampaignIDMapping.find((y) => y.model.name === x.campaign.name)?.id || -1;
    });

    //
    // Line items
    //
    const existingIDMapping = await LineItem.getEntityIDsFromModels(entities);
    entities.forEach((x) => {
      x.id = existingIDMapping.find((y) => y.model === x)?.id || -1;
    });
    return entities;
  }

  static async patchEntities(entities) {
    try {
      const lineItemsWithNewCampaigns = entities.filter((x) => x.campaign.id === -1);
      const newCampaigns = uniqBy(
        lineItemsWithNewCampaigns.map((x) => x.campaign),
        'name',
      );
      if (newCampaigns.length > 0) {
        // Save new campaigns.
        const campaigns = await rootStore.dispatch('orgApp/createCampaigns', {
          instances: newCampaigns,
          showLoader: false,
        });
        // Update line items with the correct campaign ID
        lineItemsWithNewCampaigns.forEach((li) => {
          li.campaignID = campaigns.find((c) => c.name === li.campaign.name).id;
        });
      }
      const payload = this.getPatchPayload(entities);
      await rootStore.dispatch('orgApp/patchLineItems', payload);
    } catch (e) {
      throw new Exception('Failed to patch lineItems.');
    }
  }

  static async getEntities(ids) {
    if (ids.length === 0) return [];

    try {
      const qp = QueryParams.all(FilterCollection.for('lineitem_id', ids, FilterComparisonOperator.IN));
      qp.expand = LineItem.expands;
      qp.showLoader = false;
      const getLineItemsAction = ({ queryParams, cancelTokenSource, showLoader }) =>
        rootStore.dispatch('orgApp/getLineItems', { queryParams, cancelTokenSource, showLoader });
      const response = await qp.run(getLineItemsAction);
      return response.items;
    } catch (e) {
      throw new Exception('Failed to get lineItems.');
    }
  }
}

LineItem.initialize();

export default LineItem;
